import React, { useState, useEffect, useMemo } from "react";
import LmsTable from "../ui/LmsTable";
import { useParseRouteActions, useFetchData, getDropdownSemanticOptionsFromData, safelyRunFunction, getArrayFromInputsWithProps } from "../../../helpers/GlobalHelper";
import LmsFormGenerator, { BCFormController, FormActionsType } from "../ui/LmsFormGenerator";
import { GlobalConstants, DrawsPaymentMethodOptions } from "../../constants/GlobalConstants";
import LmsTabPages from "../ui/LmsTabPages";
import { LmsInspectionCompanyMessagesTable, LmsLoanDrawDetailsTable } from "../ui/LmsSharedTables";
import { LmsEndpoints } from "../../constants/LmsEndpoints";
import BCModal from "../../ui/BCModal";
import LmsActionInput from "../ui/LmsActionInput";
import { guid, isGuid } from "../../../Helpers";
import { addOrganizationFormFields } from "../Organizations/LmsOrganizations";
import { AddInspectionCompanyInspectionsForm, EditInspectionCompanyInspectionsForm } from "../Inspections/LmsInspectionsInspectionCompanyInspections";
import { Confirm } from "semantic-ui-react";
import BCLoader from "../../ui/BCLoader";
import { LmsModifyBudget } from "./LmsServicingModifyBudgets";
import { FeatureFlags } from "../../constants/LocalServerConstants";
import { AddLoanDetailsCommentModal } from "../Loans/LmsLoanDetails";

const LOAN_DRAW_STATUSES = {
  LOAN_CO_APPROVED: "672D13F0-1767-4BD8-A48B-C4DC174762E7",
  LOAN_CO_APPROVED_REVISED: "5C4A22A3-0912-4CEA-B1F1-A8A800943EBC",
  LOAN_CO_APPROVED_IN_FUNDING: "672d13f0-1767-4bd8-a48b-c4dc174762e7",
  FUNDED: "e405e726-348a-4da9-8faa-a69100e14735"
};

interface IAddedVendorData {
  payeeOrganizationId: string;
  payeeOrganizationDisplay: string;
  detailsIndex: number;
}

const disableByDrawStatus = (ids) => (checkedItems, tableData) =>
  !(checkedItems.length === 1 && ids.includes(tableData[checkedItems[0]].loanDrawStatusId));

export const MainServicingDrawTable = ({ suffix = "draws/loan-draws", loanId = "" }) => {
  const [changeReason, setChangeReason] = useState("");
  const [checkNetSuiteRowId, setCheckNetSuiteRowId] = useState("");

  const isSummaryAchDisabled = (checkedItems, tableData) =>
    checkedItems.length === 0 || checkedItems.map((index) => tableData[index].allowAcheditId).some(value => value === "No");

    const addAction = {
      action: "add",
      path: `/servicing-draws/add${loanId ? "?loanId=" + loanId : ""}`,
    };

    const reviseAction = {
      action: "edit",
      text: "Revise",
      icon: GlobalConstants.Icons.CARET_UP,
      path: "servicing-draws/revise",
      isDisabled: disableByDrawStatus([
        LOAN_DRAW_STATUSES.LOAN_CO_APPROVED_IN_FUNDING,
        LOAN_DRAW_STATUSES.LOAN_CO_APPROVED_REVISED,
      ]),
    };

    const summaryAction = {
      action: "edit",
      text: "Summary",
      icon: GlobalConstants.Icons.CARET_UP,
      path: "servicing-draws/summary",
      isDisabled: disableByDrawStatus([
        LOAN_DRAW_STATUSES.LOAN_CO_APPROVED_IN_FUNDING,
        LOAN_DRAW_STATUSES.LOAN_CO_APPROVED_REVISED,
        LOAN_DRAW_STATUSES.FUNDED,
      ]),
    };
  
    const sendToNewAction = {
      action: "submitselected",
      text: "Send To New",
      submitSuffix: "draws/decline-draws",
      method: "PUT",
      icon: GlobalConstants.Icons.CARET_UP,
      isDisabled: disableByDrawStatus([
        LOAN_DRAW_STATUSES.LOAN_CO_APPROVED_IN_FUNDING,
        "487153a8-0baf-4808-bb49-a88c0023ed6a",
        "800f9445-d154-4995-a33f-abe600305c48",
      ]),
    };
  
    const sendToRequestedAction = {
      action: "submitselected",
      text: "Send To Requested",
      submitSuffix: "draws/decline-draws",
      method: "PUT",
      icon: GlobalConstants.Icons.CARET_UP,
      isDisabled: disableByDrawStatus([
        LOAN_DRAW_STATUSES.LOAN_CO_APPROVED_IN_FUNDING
      ])
    };
  
    const setAchTrue = { action: "setachtrue", isDisabled: isSummaryAchDisabled };
    const setAchFalse = { action: "setachfalse", isDisabled: isSummaryAchDisabled };
  
    const checkNetSuiteData = {
      text: "Check NetSuite Data",
      icon: GlobalConstants.Icons.CARET_UP,
      isDisabled: disableByDrawStatus([LOAN_DRAW_STATUSES.FUNDED]),
      handleClick: (options) => {
        setCheckNetSuiteRowId(options?.firstCheckedRow?.id || "");
      },
    };
  
    const detailedDrawSummaryAction = {
      action: "downloadselected",
      text: "Detailed Draw Summary",
      icon: GlobalConstants.Icons.CARET_UP,
      downloadSuffix: "draws/export",
      method: "POST",
      isDisabled: (checkedItems) => checkedItems?.length !== 1,
    };

  return <>
    <LmsTable
      actionButtons={[
        addAction,
        "export",
        reviseAction,
        summaryAction,
        sendToNewAction,
        sendToRequestedAction,
        setAchTrue,
        setAchFalse,
        checkNetSuiteData,
        detailedDrawSummaryAction,
        <LmsActionInput value={changeReason} handleChange={setChangeReason} placeHolder="Change Reason" /> as any
      ]}
      apiSuffix={suffix}
      setACHTrueSuffix="draws/activate-ach"
      setACHFalseSuffix="draws/deactivate-ach"
      onClickPath="servicing-draws"
      rowsBackgroundCustomColor={(row) => {
        if (row.frozen === "Yes") return "cyan";
        return row.colorId.toLowerCase();
      }}
      columnClickOptions={{
        name: {
          idProp: "id",
          path: "/servicing-draws/edit/{id}",
        }
      }}
      columnLabelsDictionary={{
        name: "Draw Name",
        dateDownDrawRequestStatus: "Date Down Status",
        titleInspectorOrganizationContact: "Title Inspector",
        workInspectorOrganizationContact: "Work Inspector",
        workInspectionDrawRequestStatus: "Work Inspection Status",
        brms: "BRMs",
        reviewCos: "Review COs",
        initialCos: "Initial COs",
        ach: "ACH",
        bcportalStatus: "BC Portal Status",
        nwccdateInspected: "Inspection Company Date Inspected",
        nwccdateReturned: "Inspection Company Date Returned",
        nwccprojectNo: "Inspection Company Project No",
        nwccsubmittedOn: "Inspection Company Submitted On",
        submitToNwcc: "Submit To Inspection Company",
        je: "JE",
        revisionReason: "Reason for Revision",
        borrowerOrganization: "Borrower",
        orderDateDownDate: "Order Date Down",
        orderDrawInspectionDate: "Order Draw Inspection",
        dateDownReceivedDate: "Date Down Received",
        builderDrawApprovedDate: "Borrower Draw Approved Date",
        inspectionReceivedDate: "Inspection Received",
        waiveDateDownDate: "Waive Date Down",
        waiveDrawInspectionDate: "Waive Draw Inspection",
        workInspectorDrawRequestStatus: "Work Inspection Status"
      }}
      useApiFiltering={suffix === "draws/loan-draws"}
    />
    {!!checkNetSuiteRowId && <BCModal
      isModalOpen={!!checkNetSuiteRowId}
      size="large"
      closeIcon
      onClose={() => setCheckNetSuiteRowId("")}
      content={<LmsTable apiSuffix={`draws/netsuite-data/${checkNetSuiteRowId}`} isDisabled />}
    />}
  </>;
};

const CommentsTable = ({ suffix, loanId, loanDrawId = "" }) => {
  const [newCommentId, setNewCommentId] = useState("");
  const commentsActionButtons = [{
    text: "Add",
    action: GlobalConstants.TableDefaultActions.MODAL_BUTTON,
    modalContent: (setIsAddFormModalOpen) => (
      <AddLoanDetailsCommentModal
        loanId={loanId}
        loanDrawId={loanDrawId}
        setIsModalOpen={setIsAddFormModalOpen}
        setNewCommentId={setNewCommentId}
      />
    ),
    isDisabled: () => false
  }];

  return <LmsTable skipClick apiSuffix={suffix} actionButtons={commentsActionButtons} refreshDataDependency={newCommentId} />;
};

const HistoryTable = ({ suffix }) => {
  return <LmsTable skipClick apiSuffix={suffix} actionButtons={["export"]} />;
};

const ReviseTabPages = ({ id = "", loanId, loanDrawTempId }) => {
  let tabPages: any;

  if (loanDrawTempId) {
    tabPages = {
      Comments: <CommentsTable suffix={`draws/comments/${id}`} loanId={loanId} loanDrawId={id} />,
      Documents: <LmsTable apiSuffix={`draws/loan-draw-temp-document/${loanDrawTempId}`} skipClick actionButtons={["export"]} />,
      History: <LmsTable skipClick apiSuffix={`draws/history/${id}`} actionButtons={["export"]} />,
    };
  }

  return <>
    {loanDrawTempId && <LmsTabPages tabPages={tabPages} />}
  </>;
};

const AddDrawTabPages = ({ loanDrawTempId, loanDrawId = "", loanId }) => {
  let tabPages: any;

  if (loanDrawTempId) {
    tabPages = {
      Comments: <CommentsTable suffix={`draws/comments/${loanDrawTempId}`} loanId={loanId} loanDrawId={loanDrawId} />,
      History: <LmsTable skipClick apiSuffix={`draws/history/${loanDrawTempId}`} actionButtons={["export"]} />,
    };
  }

  return <>
    {loanDrawTempId && <LmsTabPages tabPages={tabPages} />}
  </>;
};

const AddExistingLineTable = ({ loanDrawTempId, setAddExistingIsVisible, useFormInstance, getUpdatedTempData, revisedDrawDetails, setData }) => {
  const [isTableDisplayed, setIsTableDisplayed] = useState(true);
  const onSuccess = (response) => {
    setIsTableDisplayed(false);
    setIsTableDisplayed(true);
    if (response && response.checkedRowsData && response.checkedRowsData.length > 0) {
      const existingLinesData: any[] = response.checkedRowsData.map(
        ({
           loanBudgetCategoryId,
           loanBudgetCategory,
           amount,
           loanBudgetSubCategory,
           disbursedAmount,
           vendor,
           loanBudgetDetailId,
           description
         }) => {

          return {
            loanBudgetDetailId: loanBudgetDetailId,
            loanBudgetCategoryDisplay: loanBudgetCategory || "",
            loanBudgetSubCategoryDisplay: loanBudgetSubCategory || "",
            detailDirectPay: false,
            availablePreviouslyInspected: amount,
            drawRequestedAmount: 0,
            disbursedAmount: disbursedAmount,
            description: description,
            drawRevisionAmount: 0,
            drawApprovedAmount: amount,
            loanBudgetCategoryId,
            loanDrawTempBudgetDetailVendors: [{ paymentMethodOption: "ACH", payeeOrganizationId: vendor, requestedAmount: 0 }],
          };
        });

      const revisedWithNewLines = {
        ...revisedDrawDetails,
        loanDrawTempBudgetDetails: [...revisedDrawDetails.loanDrawTempBudgetDetails, ...existingLinesData]
      };

      const updatedTempData = getUpdatedTempData(revisedWithNewLines, useFormInstance);
      setData(updatedTempData);
      useFormInstance.setMetaData({ reviseData: updatedTempData });
    }
  };

  const addExistingLineActions = [
    {
      action: "submitselected",
      text: "Add Selected",
      icon: GlobalConstants.Icons.SAVE,
      submitSuffix: `draws/bulkadd-temp-budget-details-vendors/${loanDrawTempId}`,
      onSuccessFunc: onSuccess,
      refreshDataOnSuccess: true
    },
    { action: "close", handleClick: () => setAddExistingIsVisible(false) }
  ];

  return <>
    {isTableDisplayed && <LmsTable
      apiSuffix={`draws/loan-budget-details/${loanDrawTempId}`}
      actionButtons={addExistingLineActions}
    />}
  </>;
};

const AddVendorToReviseDrawForm = ({ reviseData, setIsAddVendorOpen, setReviseData, reviseFormOptions, getUpdatedTempData, detailNestedInputsProp, value = "" }) => {
  const [{ data, isLoading }] = useFetchData("draws/payee-organizations");
  const vendorOptions = useMemo(() => {
    if (data) {
      return getDropdownSemanticOptionsFromData(data, "name");
    }

    return [];
  }, [data]);

  const myForm = useMemo(() => {
    if (data) {
      return setAddVendorFields(new BCFormController(value ? { payeeOrganizationId: value } : {}, { styles: { minHeight: "450px" } }));
    }

    return new BCFormController({}, { styles: { minHeight: "450px" } });
  }, [data]);

  const formActions = [{
    action: "save-close",
    text: "Save and Close",
    icon: GlobalConstants.Icons.SAVE
  }, "close"];

  const handleSubmit = (data) => {
    const addedVendor = {};
    if (data) {
      const { payeeOrganizationId } = data;
      if (payeeOrganizationId) {
        addedVendor["payeeOrganizationId"] = payeeOrganizationId;
        addedVendor["payeeOrganizationDisplay"] = vendorOptions.find((option) => option.value === payeeOrganizationId)?.text || "";
        addedVendor["detailsIndex"] = detailNestedInputsProp;
      }
    }

    const updatedReviseData = getUpdatedTempData(reviseData, reviseFormOptions, addedVendor, value);
    setReviseData(updatedReviseData);
    reviseFormOptions.setMetaData({ reviseData: updatedReviseData });
    setIsAddVendorOpen(false);
  };

  return <LmsFormGenerator
    myForm={myForm}
    isLoading={isLoading}
    actions={formActions}
    handleSubmit={handleSubmit}
    handleClose={() => setIsAddVendorOpen(false)}
  />;
};

const hasSelectedRow = (loanDrawTempDetails) => {
  if (loanDrawTempDetails) {
    return loanDrawTempDetails.some((rowData) => {
      return rowData?.isSelected;
    });
  }

  return false;
};

const VoidReasonForm = ({ values = {}, setIsVoidFormModalOpen }) => {
  const myForm = new BCFormController({}, { values, submitSuffix: "draws/void-draw", payloadDataType: "FormData" });
  myForm.addCenteredGroup([myForm.getTextareaField("Void Reason", { required: true })]);
  myForm.addCenteredGroup([myForm.getFileField("Void Document")]);
  const formActions = [{ action: "save", text: "Void Draw" }, "close"];
  return <LmsFormGenerator myForm={myForm} actions={formActions} handleClose={() => setIsVoidFormModalOpen(false)} displayMessageLocally />;
};

const ReviseLoanDraw = ({ id }) => {
  const [isDrawExceedsPromptOpen, setIsDrawExceedsPromptOpen] = useState(false);
  const [isVoidFormModalOpen, setIsVoidFormModalOpen] = useState(false);
  const [voidValues, setVoidValues] = useState({} as any);
  const [{ data: revisedDrawDetails, isLoading, error, setData, setError, fetchData }] = useFetchData(`draws/${id}/revised`);
  const loanDrawBudgetDetails = revisedDrawDetails?.loanDrawTempBudgetDetails;
  const loanDrawTempId = revisedDrawDetails?.id || "";
  const loanId = revisedDrawDetails?.loanId || "";
  const loanPropertyId = revisedDrawDetails?.loanPropertyId || "";
  const bankInformationId = revisedDrawDetails?.bankInformationId || "";
  const bankInfoTableSuffix = bankInformationId ? `draws/bank-info?loanDrawId=${id}&bankInformationId=${revisedDrawDetails?.bankInformationId}` : "";
  const vendorRevisionAmounts = useMemo(() => {
    let tempAmount = [] as any[];
    revisedDrawDetails?.loanDrawTempBudgetDetails?.forEach((vendorDetail) => {
      if (tempAmount.length === 0) {
        vendorDetail?.loanDrawTempBudgetDetailVendors?.forEach((vendor) => {
          if (vendor?.loanDrawDetailVendorRevisionsAmount?.length > 0) {
            tempAmount = vendor?.loanDrawDetailVendorRevisionsAmount;
          }
        });
      }
    });

    return tempAmount;
  }, [revisedDrawDetails]);

  const handleVoidDrawClick = (options) => {
    const confirmVoidPayload = {
      loanDrawId: id,
      inspectionFee: revisedDrawDetails?.inspectionFee,
      dateDownFee: revisedDrawDetails?.dateDownFee
    };

    options.handleConfirmFunc(async () => {
      const { data: resData, error: resError } = await fetchData("draws/confirm-void-draw", {
        fetchMethod: "POST",
        data: confirmVoidPayload
      });

      if (resError) {
        setError(resError);
      }
      else {
        if (resData.confirmMessage) {
          if (confirm(resData.confirmMessage)) {
            setVoidValues({ ...confirmVoidPayload, loanDrawTempId });
            setIsVoidFormModalOpen(true);
          }
        }
        else {
          setVoidValues({ ...confirmVoidPayload, loanDrawTempId });
          setIsVoidFormModalOpen(true);
        }
      }

      options.setConfirmOptions({});
    }, "Are you sure you want to Void this Draw?");
  };

  const formatBankDataFunc = (data) => {
    if (data?.bankInfoData) {
      return data?.bankInfoData;
    }

    return [];
  };

  const getUpdatedVendorRowDetails = (inputs, index, vendorDetailsCopy = [] as any[], setInputs, addedVendor = {} as IAddedVendorData) => {
    const remainingVendorDetails = [...vendorDetailsCopy];
    const updatedVendorDetails = [] as any[];
    const { payeeOrganizationId, payeeOrganizationDisplay, detailsIndex } = addedVendor;
    Object.entries(inputs).forEach(([key, value]: [any, any]) => {
      if (key.startsWith(`${index}-`)) {
        const vendorDetailIndex = key.split("-")[1];
        const vendorDetailOriginal = vendorDetailsCopy[vendorDetailIndex] || {};
        const revisionAmount = value?.revisionAmount ? parseFloat(value?.revisionAmount) : undefined;
        const vendorDetailWithFormInputs = { ...vendorDetailOriginal, ...value, revisionAmount };
        if (payeeOrganizationId && payeeOrganizationDisplay && detailsIndex === index && parseInt(vendorDetailIndex) === vendorDetailsCopy.length - 1) {
          const updatedVendorDetail = { ...vendorDetailWithFormInputs, payeeOrganizationDisplay, payeeOrganizationId };
          if (!vendorDetailOriginal.payeeOrganizationId) {
            updatedVendorDetails.push(updatedVendorDetail);
            if (setInputs) {
              setInputs({
                ...inputs,
                [key]: updatedVendorDetail,
              });
            }
          }
          else {
            updatedVendorDetails.push({ ...vendorDetailWithFormInputs, isSelected: false });
            updatedVendorDetails.push({ payeeOrganizationDisplay, payeeOrganizationId, isSelected: true });
          }
        }
        else {
          updatedVendorDetails.push(vendorDetailWithFormInputs);
        }

        remainingVendorDetails.splice(vendorDetailIndex, 1);
      }
    });

    if (!payeeOrganizationId && remainingVendorDetails.length > 0) {
      updatedVendorDetails.push(...remainingVendorDetails);
    }

    return updatedVendorDetails;
  };

  const getUpdatedTempData = (loanDrawRevisedDetails = revisedDrawDetails, formOptions, addedVendor = {} as IAddedVendorData) => {
    const { inputs, setInputs, metaData } = formOptions;
    const reviseData = metaData?.reviseData || loanDrawRevisedDetails;
    const loanDrawRevisedBudgetDetails = reviseData?.loanDrawTempBudgetDetails || [];
    return {
      ...reviseData,
      loanDrawTempBudgetDetails: loanDrawRevisedBudgetDetails.map((budgetDetail, index) => {
        const vendorDetailsCopy = budgetDetail.loanDrawTempBudgetDetailVendors;
        const detailsInputs = inputs ? inputs[index] || {} : {};
        return {
          ...budgetDetail,
          ...detailsInputs,
          loanDrawTempBudgetDetailVendors: getUpdatedVendorRowDetails(inputs, index, vendorDetailsCopy, setInputs, addedVendor)
        };
      })
    };
  };

  const getBudgetDetailsIdsWithApprovedAmount = (inputs) => {
    if (loanDrawBudgetDetails) {
      return loanDrawBudgetDetails.map((rowData, i) => {
        const approvedAmount = inputs[i].drawApprovedAmount !== undefined ? parseFloat(inputs[i].drawApprovedAmount) : 0;
        return {
          loanBudgetDetailId: rowData.id,
          approvedAmount
        };
      });
    }

    return [];
  };

  const myForm = new BCFormController(revisedDrawDetails, { isCompact: true, styles: { minWidth: "2500px" } });
  myForm.addGroupFields([
    myForm.getDropdownDisplayField("Borrower", { name: "borrowerOrganizationName" }),
    myForm.getDropdownDisplayField("Loan", { name: "loanDisplay" }),
    myForm.getDropdownDisplayField("Loan Property", { name: "loanPropertyDisplay" }),
    myForm.getCheckboxField("Direct Pay")
  ]);
  myForm.addGroupFields([
    myForm.getTextareaField("Description"),
  ]);
  myForm.addDividerField({ dividerIsHidden: true });
  myForm.addGroupFields([
    myForm.getDropdownDisplayField("Bank", { name: "bankInformationDisplay", width: 4 }),
    myForm.getComponent(<LmsTable formatDataFunc={formatBankDataFunc} apiSuffix={bankInfoTableSuffix} key="bank-info-table" isDisabled />, { width: 12 })
  ], { widths: 1 });
  myForm.addDividerField();
  myForm.addGroupFields([
    myForm.getActionField("Draw Name", { actionFunc: handleVoidDrawClick, actionLabel: "Void Draw", actionIcon: GlobalConstants.Icons.CARET_UP }, {
      width: 3,
      name: "loanDrawName",
      inline: true,
      disabled: true
    }),
  ], { widths: 2 });

  const labelFields = [] as any[];
  labelFields.push(myForm.getLabelField("Budget Category", { width: 3, position: "center" }));
  labelFields.push(myForm.getLabelField("Sub-Category", { width: 2, position: "center" }));
  labelFields.push(myForm.getLabelField("Direct Pay", { width: 1, position: "center" }));
  labelFields.push(myForm.getLabelField("Vendor", { width: 2, position: "center" }));
  labelFields.push(myForm.getLabelField("Is Paid", { width: 1, position: "center" }));
  labelFields.push(myForm.getLabelField("Paid By", { width: 1, position: "center" }));
  labelFields.push(myForm.getLabelField("Requested Amount", { width: 2, position: "center" }));
  labelFields.push(myForm.getLabelField("Approved Amount", { width: 2, position: "center" }));
  labelFields.push(myForm.getLabelField("Available (Previously Inspected)", { width: 2, wrapLabel: true, position: "center" }));
  labelFields.push(myForm.getLabelField("Budget Remaining", { width: 2, position: "center" }));

  if (vendorRevisionAmounts?.length > 0) {
    for (let i = 0; i < vendorRevisionAmounts.length; i++) {
      labelFields.push(myForm.getLabelField(`Revision ${i + 1}`, { width: 2, position: "center" }));
    }

    labelFields.push(myForm.getLabelField(`Revision ${vendorRevisionAmounts?.length + 1}`, { width: 2, position: "center" }));
  }
  else {
    labelFields.push(myForm.getLabelField("Revision", { width: 2, position: "center" }));
  }

  labelFields.push(myForm.getLabelField("Invoice #/ Reference", { width: 2, position: "center" }));
  labelFields.push(myForm.getLabelField("Comments", { width: 2, position: "center" }));
  myForm.addGroupFields(labelFields, { widths: 1 });

  if (loanDrawBudgetDetails) {
    loanDrawBudgetDetails.map((rowData, i) => {
      const {
        loanBudgetCategoryDisplay,
        loanBudgetSubCategoryDisplay,
        detailDirectPay,
        drawRequestedAmount,
        disbursedAmount = 0,
        description,
        drawRevisionAmount,
        drawApprovedAmount = 0,
        loanDrawTempBudgetDetailVendors,
        budgetAmount
      } = rowData;

      const budgetRemaining = budgetAmount - disbursedAmount;
      const formRowFields = [] as any[];
      formRowFields.push(myForm.getLabelField(loanBudgetCategoryDisplay, { width: 3, position: "top-left", wrapLabel: true, fontWeightLight: true }));
      formRowFields.push(myForm.getLabelField(loanBudgetSubCategoryDisplay, { width: 2, position: "top-left", wrapLabel: true, fontWeightLight: true }));
      formRowFields.push(myForm.getCheckboxField("Direct Pay", { label: "", value: detailDirectPay, width: 1, position: "center", disabled: true, nestedInputsProp: i }));
      formRowFields.push(myForm.getButtonField("", undefined, {
        modalContent: (setIsAddFormModalOpen, options) => (
          <AddVendorToReviseDrawForm
            reviseData={revisedDrawDetails}
            setIsAddVendorOpen={setIsAddFormModalOpen}
            reviseFormOptions={options}
            getUpdatedTempData={getUpdatedTempData}
            detailNestedInputsProp={i}
            setReviseData={setData}
          />
        ),
        buttonIcon: GlobalConstants.Icons.ADD,
        width: 2,
        nestedInputsProp: i
      }));
      formRowFields.push(myForm.getBlankField({ width: 1 }));
      formRowFields.push(myForm.getBlankField({ width: 1 }));
      formRowFields.push(myForm.getCurrencyField("Requested Amount", {
        label: "",
        name: "drawRequestedAmount",
        value: drawRequestedAmount,
        calculatedTargetField: "requestedAmountTotal",
        width: 2,
        disabled: true,
        nestedInputsProp: i
      }));
      formRowFields.push(myForm.getCurrencyField("Amount Approved", {
        label: "",
        name: "drawApprovedAmount",
        calculatedTargetField: "approvedAmountTotal",
        value: drawApprovedAmount,
        width: 2,
        disabled: true,
        nestedInputsProp: i
      }));
      formRowFields.push(myForm.getCurrencyField("Available (Previously Inspected)", {
        label: "",
        name: "availablePreviouslyInspected",
        value: budgetAmount,
        width: 2,
        disabled: true,
        nestedInputsProp: i
      }));
      formRowFields.push(myForm.getCurrencyField("Budget Remaining", {
        label: "",
        name: "budgetRemaining",
        value: budgetRemaining,
        width: 2,
        disabled: true,
        nestedInputsProp: i
      }));

      if (vendorRevisionAmounts?.length > 0) {
        vendorRevisionAmounts.map(() => {
          formRowFields.push(myForm.getBlankField({ width: 2 }));
        });
      }

      formRowFields.push(myForm.getCurrencyField("Revision", {
        label: "",
        name: "drawRevisionAmount",
        value: drawRevisionAmount,
        calculatedTargetField: "revisionAmountTotal",
        width: 2,
        isDisabledOn: { isSelected: true },
        isDisabledOnOperation: "notequal",
        nestedInputsProp: i
      }));
      formRowFields.push(myForm.getBlankField({ width: 2 }));
      formRowFields.push(myForm.getInputField("Comments", { label: "", name: "description", value: description, width: 2, nestedInputsProp: i }));
      myForm.addGroupFields(formRowFields, { widths: 1 });

      const vendors = loanDrawTempBudgetDetailVendors || [];
      if (vendors.length === 0) {
        vendors.push({
          approvedAmount: drawApprovedAmount,
          description: "",
          drawReference: null,
          isPaid: false,
          isSelected: false,
          paymentMethodOption: "",
          revisionAmount: drawRevisionAmount,
          requestedAmount: drawRequestedAmount,
        });
      }

      if (vendors?.length > 0) {
        vendors.map((vendorDetail, vendorIndex) => {
          const {
            approvedAmount: approvedVendorAmount,
            description: descriptionVendor,
            drawReference: drawReference,
            isPaid,
            isSelected,
            paymentMethodOption: paymentMethodOptionVendor,
            revisionAmount,
            requestedAmount: requestedAmountVendor,
            payeeOrganizationDisplay,
            payeeOrganizationId,
            loanDrawDetailVendorRevisionsAmount
          } = vendorDetail;
          const nestedInputsProp = `${i}-${vendorIndex}`;
          const vendorDetailsRowFields = [] as any[];
          vendorDetailsRowFields.push(myForm.getCheckboxField("", { name: "isSelected", value: isSelected, width: 3, inlineRight: true, nestedInputsProp }));
          vendorDetailsRowFields.push(myForm.getBlankField({ width: 2 }));
          vendorDetailsRowFields.push(myForm.getBlankField({ width: 1 }));
          vendorDetailsRowFields.push(myForm.getButtonField("", undefined, {
            name: "payeeOrganizationDisplay",
            value: payeeOrganizationDisplay,
            width: 2,
            modalContent: () => <LmsTable actionButtons={["export"]} apiSuffix={`orgs/bank-infolist/${payeeOrganizationId}`} />,
            modalSize: "fullscreen",
            buttonRenderType: "link"
          }));
          vendorDetailsRowFields.push(myForm.getCheckboxField("", { name: "isPaid", value: isPaid, width: 1, nestedInputsProp, position: "center" }));
          vendorDetailsRowFields.push(myForm.getLabelField(paymentMethodOptionVendor, { width: 1, position: "center", fontWeightLight: true }));
          vendorDetailsRowFields.push(myForm.getCurrencyField("Requested Amount", {
            label: "",
            name: "requestedAmount",
            value: requestedAmountVendor,
            calculatedTargetField: "requestedAmountTotal",
            width: 2,
            disabled: true,
            nestedInputsProp
          }));
          vendorDetailsRowFields.push(myForm.getCurrencyField("Amount Approved", {
            label: "",
            name: "drawApprovedAmount",
            calculatedTargetField: "approvedAmountTotal",
            value: approvedVendorAmount,
            width: 2,
            disabled: true,
            nestedInputsProp
          }));
          vendorDetailsRowFields.push(myForm.getBlankField({ width: 2 }));
          vendorDetailsRowFields.push(myForm.getBlankField({ width: 2 }));
          const vendorDetailAmounts: any[] = loanDrawDetailVendorRevisionsAmount || [];
          if (vendorDetailAmounts?.length === 0 && vendorRevisionAmounts?.length > 0) {
            for (let i = 0; i < vendorRevisionAmounts?.length; i++) {
              vendorDetailAmounts.push(0);
            }
          }

          if (vendorDetailAmounts?.length > 0) {
            vendorDetailAmounts.map((revAmount, revIndex) => {
              vendorDetailsRowFields.push(myForm.getCurrencyField("Revision", {
                label: "",
                name: `revisionAmount-${revIndex}`,
                value: revisionAmount,
                calculatedTargetField: `revisionAmountTotal${revIndex}`,
                width: 2,
                disabled: true,
                nestedInputsProp
              }));
            });
          }

          vendorDetailsRowFields.push(myForm.getCurrencyField("Revision", {
            label: "",
            name: "revisionAmount",
            value: revisionAmount,
            calculatedTargetField: "revisionAmountTotal",
            width: 2,
            isDisabledOn: { isSelected: true },
            isDisabledOnOperation: "notequal",
            nestedInputsProp
          }));
          vendorDetailsRowFields.push(myForm.getInputField("Invoice #/ Reference", {
            label: "",
            name: "drawReference",
            value: drawReference,
            width: 2,
            disabled: true,
            nestedInputsProp
          }));
          vendorDetailsRowFields.push(myForm.getInputField("Comments", { label: "", name: description, value: descriptionVendor, width: 2, nestedInputsProp }));
          myForm.addGroupFields(vendorDetailsRowFields, { widths: 1, nestedInputsProp });
        });
      }
    });
  }

  const calculateVendorsTotal = (_, options) => {
    let total = 0;
    options.formFields?.map(({ groupFields }) => {
      if (groupFields && groupFields[3] && groupFields[3].name === "loanDrawDetailVendorId") {
        total++;
      }
    });

    return total;
  };

  const totalsFormRowFields = [] as any[];
  totalsFormRowFields.push(myForm.getBlankField({ width: 2 }));
  totalsFormRowFields.push(myForm.getBlankField({ width: 1 }));
  totalsFormRowFields.push(myForm.getBlankField({ width: 1 }));
  totalsFormRowFields.push(myForm.getLabelField("Vendor Total", { position: "center", width: 2 }));
  totalsFormRowFields.push(myForm.getNumberField("", {
    label: "",
    name: "calculatedVendorsTotal",
    calculatedFunc: calculateVendorsTotal,
    width: 2,
    disabled: true
  }));
  totalsFormRowFields.push(myForm.getBlankField({ width: 1 }));
  totalsFormRowFields.push(myForm.getLabelField("Totals", { position: "center", width: 1 }));
  totalsFormRowFields.push(myForm.getCurrencyField("Requested Amount Total", { label: "", name: "requestedAmountTotal", width: 2, disabled: true }));
  totalsFormRowFields.push(myForm.getCurrencyField("Approved Amount Total", { label: "", width: 2, disabled: true }));
  totalsFormRowFields.push(myForm.getBlankField({ width: 2 }));
  totalsFormRowFields.push(myForm.getBlankField({ width: 2 }));
  if (vendorRevisionAmounts?.length > 0) {
    for (let i = 0; i < vendorRevisionAmounts?.length; i++) {
      totalsFormRowFields.push(myForm.getCurrencyField("", { name: `revisionAmountTotal${i}`, width: 2, disabled: true }));
    }
  }

  totalsFormRowFields.push(myForm.getCurrencyField("Revision Amount Total", { label: "", width: 2, disabled: true }));
  totalsFormRowFields.push(myForm.getBlankField({ width: 2 }));
  totalsFormRowFields.push(myForm.getBlankField({ width: 2 }));
  myForm.addGroupFields(totalsFormRowFields, { widths: 1 });
  myForm.addDividerField();
  myForm.addGroupFields([
    myForm.getBlankField({ width: 3 }),
    myForm.getTextareaField("Reason for Revision", { width: 8 }),
    myForm.getDisplayField("Title", { width: 2 }),
    myForm.getBlankField({ width: 3 }),
  ], { widths: 1 });

  const handleSubmit = async (inputs, options) => {
    const useFormInstance = options?.useFormInstance;
    if (hasSelectedRow(revisedDrawDetails?.loanDrawTempBudgetDetails || [])) {
      if (useFormInstance?.initSubmittingStates) {
        useFormInstance?.initSubmittingStates(options.buttonText);
      }

      const validateDrawPayload = {
        loanDrawTempId: revisedDrawDetails?.id,
        orderDateDownDate: new Date().toLocaleDateString(),
        totalDrawAmount: inputs?.requestedAmountTotal,
        budgetDetailIdWithApprovedAmount: getBudgetDetailsIdsWithApprovedAmount(inputs),
      };

      const { data, error } = await fetchData("draws/validate-approve", { fetchMethod: "POST", data: validateDrawPayload });
      if (error) {
        useFormInstance.setFormErrorMessage(error);
        safelyRunFunction(useFormInstance?.resetSubmittingStates);
      }

      if (!error && data) {
        if (!data.existsCommitmentCap) {
          options.handleSubmit("draws/approve", { method: "PUT", data: getUpdatedTempData(revisedDrawDetails, options), buttonText: options.buttonText });
        }
        else {
          safelyRunFunction(useFormInstance?.resetSubmittingStates);
          setIsDrawExceedsPromptOpen(true);
        }
      }
    }
    else {
      useFormInstance.setFormErrorMessage({ message: "Please Select a line item to Revise." });
    }
  };

  const handleRefresh = async (inputs, _, options) => {
    if (options?.initSubmittingStates) {
      options?.initSubmittingStates(options.buttonText);
    }

    const { error } = await fetchData("draws/refresh-temp", { fetchMethod: "PUT", data: getUpdatedTempData(revisedDrawDetails, options) });
    if (error) {
      options.setFormErrorMessage(error);
    }

    safelyRunFunction(options?.resetSubmittingStates);
  };

  const formActions: FormActionsType = [
    { text: "Modify Budget", path: `/servicing-modify-budgets/${loanPropertyId}` },
    { text: "Refresh", handleClick: handleRefresh, icon: GlobalConstants.Icons.SAVE },
    {
      text: "Add Existing Line",
      modalContent: (setIsModalOpen, formOptions) => {
        return (
          <AddExistingLineTable
            loanDrawTempId={loanDrawTempId}
            setAddExistingIsVisible={setIsModalOpen}
            useFormInstance={formOptions}
            getUpdatedTempData={getUpdatedTempData}
            revisedDrawDetails={revisedDrawDetails}
            setData={setData}
          />
        );
      },
      modalSize: "fullscreen",
      icon: GlobalConstants.Icons.SAVE
    },
    { action: "save", text: "Save and Approve" },
    "close"
  ];

  return <>
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      error={error}
      actions={formActions}
      handleSubmit={handleSubmit}
    />
    {loanId && <ReviseTabPages id={id} loanDrawTempId={loanDrawTempId} loanId={loanId} />}
    <BCModal
      isModalOpen={isDrawExceedsPromptOpen}
      onClose={() => setIsDrawExceedsPromptOpen(false)}
      closeIcon
      content="Draw Request exceeds the commitment cap for this loan. Please review commitment cap and see servicing team."
      buttonText="Ok"
    />
    <BCModal
      isModalOpen={isVoidFormModalOpen}
      onClose={() => setIsVoidFormModalOpen(false)}
      closeIcon
      content={<VoidReasonForm values={voidValues} setIsVoidFormModalOpen={setIsVoidFormModalOpen} />}
    />
  </>;
};

const SummarySubpage = ({ id }) => {
  const [summaryComments, setSummaryComments] = useState([]);
  const [isViewAllUploadedInvoicesVisible, setIsViewAllUploadedInvoicesVisible] = useState(false);
  const [isViewOriginalDrawVisible, setIsViewOriginalDrawVisible] = useState(false);
  const [{ data = { loanPhaseDisplay: "", loanStatusDisplay: "" }, isLoading, error }] =
    useFetchData(`draws/${id}/summary`);
  const isPaidOffAndInactive =
    data.loanPhaseDisplay.toLowerCase() === "paid off" &&
    data.loanStatusDisplay.toLowerCase() === "inactive";

  const myForm = new BCFormController(
    data,
    {
      errorTitle: isPaidOffAndInactive ? "Loan is in Paid Off / Inactive status" : undefined,
      values: { Id: id },
      excludeFromSubmit: ["id"],
      submitSuffix: "draws/summary",
    }
  );

  myForm.addGroupFields([
    myForm.getDisplayField("Loan", { name: "loanNumber" }),
    myForm.getDropdownDisplayField("Borrower", { name: "borrowerOrganizationName" }),
    myForm.getDisplayField("Loan Owner"),
    myForm.getDisplayField("Draw Name", { name: "name" }),
  ]);
  myForm.addGroupFields([
    myForm.getCurrencyField("Amount Requested", { disabled: true }),
    myForm.getCurrencyField("Amount Approved", { disabled: true }),
    myForm.getCalendarField("Approved Date", { disabled: true }),
    myForm.getDisplayField("Title #", { name: "titleNumber" })
  ]);
  myForm.addDividerField({ dividerIsHidden: true });
  if (data.name) {
    myForm.addCenteredGroup([
      myForm.getComponent(
        <LmsTable
          key="historyTable"
          apiSuffix={`draws/${id}/history-data`}
          columnLabelsDictionary={{
            drawAmount: data.name
          }}
          sortByArray={["lineItem", "budget", "requested", "drawAmount", "vendor"]}
          isDisabled
        />,
        { width: 8 }
      ),
      myForm.getComponent(
        <LmsTable
          isDisabled
          key="transactionTable"
          apiSuffix={`draws/${id}/transaction-data`}
          sortByArray={["transactionType", "effectiveDate", "amount"]}
        />,
        { width: 8 }
      ),
    ]);
  }

  myForm.addDividerField({ dividerIsHidden: true });
  myForm.addGroupFields([
    myForm.getBlankField(),
    myForm.getButtonField("View All Upload Invoices", () => setIsViewAllUploadedInvoicesVisible(true)),
    myForm.getButtonField("View Original Draw Request", undefined, {
      modalContent: (setIsModalOpen) => <ViewOriginalDraw id={id} setIsModalOpen={setIsModalOpen} key="view-original-draw-modal" />,
      modalSize: "fullscreen",
    }),
    myForm.getDownloadButtonField("Download Draw Management", `draws/export-histories?loanId=${data?.loanId}`),
    myForm.getBlankField()
  ]);
  myForm.addGroupFields([
    myForm.getBlankField(),
    myForm.getLabelField("Date Down", { position: "center" }),
    myForm.getBlankField(),
    myForm.getLabelField("Inspection", { position: "center" }),
    myForm.getBlankField()
  ]);
  myForm.addDividerField();
  myForm.addGroupFields([
    myForm.getBlankField(),
    myForm.getCalendarField("Date Down Order", { name: "orderDateDownDate", disabled: true }),
    myForm.getBlankField(),
    myForm.getCalendarField("Inspection Order Date", { name: "orderDrawInspectionDate", disabled: true }),
    myForm.getBlankField()
  ]);
  myForm.addGroupFields([
    myForm.getBlankField(),
    myForm.getCalendarField("Date Down Waived", { name: "waiveDateDownDate", disabled: true }),
    myForm.getBlankField(),
    myForm.getCalendarField("Inspection Waived Date", { name: "waiveDrawInspectionDate", disabled: true }),
    myForm.getBlankField()
  ]);
  myForm.addGroupFields([
    myForm.getBlankField(),
    myForm.getCurrencyField("Date Down Fee", { required: true }),
    myForm.getBlankField(),
    myForm.getCurrencyField("Inspection Fee", { required: true }),
    myForm.getBlankField()
  ]);
  myForm.addGroupFields([
    myForm.getBlankField(),
    myForm.getCurrencyField("Draw Processing Fee", { required: true }),
    myForm.getBlankField(),
    myForm.getCurrencyField("Inspection Site Visit Fee"),
    myForm.getBlankField()
  ]);
  myForm.addGroupFields([
    myForm.getBlankField(),
    myForm.getTextareaField("dateDescription", { label: "" }),
    myForm.getBlankField(),
    myForm.getTextareaField("inspectionDescription", { label: "" }),
    myForm.getBlankField()
  ]);
  myForm.addDividerField();
  setInspectionSectionFields(myForm);
  if (summaryComments.length > 0) {
    myForm.addGroupFields([
      myForm.getLabelField("Date", { width: 2 }),
      myForm.getLabelField("Comments", { width: 4 }),
    ]);
    summaryComments.forEach((loanComment: { createdOn: string; description: string; }) => {
      myForm.addGroupFields([
        myForm.getCalendarField("Date", {
          width: 2,
          label: "",
          disabled: true,
          value: loanComment.createdOn,
        }),
        myForm.getTextareaField("Comments", {
          width: 4,
          label: "",
          disabled: true,
          value: loanComment.description,
        }),
      ]);
    });
  }
  myForm.addDividerField();
  myForm.addGroupFields([myForm.getTextareaField("Draw Comments")]);

  useEffect(() => {
    if (Array.isArray(data.loanComments) && data.loanComments.length > 0) {
      setSummaryComments(
        data.loanComments.reduce(
          (acc, loanComment) =>
            loanComment.commentCategorySubjectDisplay ===
            "Servicing - Draws - Individual Processing"
              ? acc.concat([loanComment])
              : acc,
          []
        )
      );
    }
  }, [data]);

  return <React.Fragment>
    <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={(data.processedOn || isPaidOffAndInactive) ? ["close"] : ["save","save-close", "close"]} />
    {isViewAllUploadedInvoicesVisible && <BCModal
      content={<LmsTable apiSuffix={`draws/document/${id}`} actionButtons={["export"]} />}
      isModalOpen={isViewAllUploadedInvoicesVisible}
      onClose={() => setIsViewAllUploadedInvoicesVisible(false)}
      closeIcon={true}
    />}
    {isViewOriginalDrawVisible && <BCModal
      content={<ViewOriginalDraw id={id} />}
      isModalOpen={isViewOriginalDrawVisible}
      onClose={() => setIsViewOriginalDrawVisible(false)}
      size={"fullscreen"}
      closeIcon={true}
    />}
  </React.Fragment>;
};

const AddVendorToAddDrawForm = ({ setIsAddVendorOpen, options, loanDrawTempData, setLoanDrawTempData, setInputsCopy }) => {
  const myForm = setAddVendorFields(new BCFormController());
  const formActions = ["save-close", "close"];

  const handleSubmit = (data) => {
    const drawInputs = options.useFormInstance.inputs;
    const setDrawInputs = options.useFormInstance.setInputs;
    const nestedInputsProp = options.nestedInputsProp;
    const fieldName = options.name;
    const fieldValue = data ? data[fieldName] : null;
    const updatedDrawInputs = { ...drawInputs };
    if (nestedInputsProp !== undefined && drawInputs[nestedInputsProp]) {
      if (options.useFormInstance.inputs && options.useFormInstance.inputs[nestedInputsProp] && !options.useFormInstance.inputs[nestedInputsProp][fieldName]) {
        updatedDrawInputs[nestedInputsProp][fieldName] = fieldValue;
        setDrawInputs(updatedDrawInputs);
        setInputsCopy(updatedDrawInputs);
      }
      else {
        const loanDrawTempDataDetails = [...loanDrawTempData?.loanDrawTempDetails];
        const loanDrawTempDataDetailsCopyTemp = [...loanDrawTempDataDetails];
        const loanDrawTempDetailsRowCopy = {
          ...loanDrawTempDataDetails[nestedInputsProp],
          [fieldName]: fieldValue,
          directPay: true,
          paidBy: "ACH",
          description: ""
        };

        loanDrawTempDataDetailsCopyTemp?.splice(nestedInputsProp + 1, 0, loanDrawTempDetailsRowCopy);
        if (setLoanDrawTempData) {
          setLoanDrawTempData({ ...loanDrawTempData, loanDrawTempDetails: loanDrawTempDataDetailsCopyTemp });
        }

        const { updatedArr, inputsWithoutProps: inputsUpdated } = getArrayFromInputsWithProps(drawInputs);
        const activeRowCopy = {
          ...drawInputs[nestedInputsProp],
          [fieldName]: fieldValue,
          directPay: true,
          paidBy: "ACH",
          description: ""
        };

        updatedArr?.splice(nestedInputsProp + 1, 0, activeRowCopy);
        if (updatedArr.length > 0) {
          updatedArr.map((inp, i) => {
            inputsUpdated[i] = inp;
          });
        }

        setDrawInputs(inputsUpdated);
        setInputsCopy(inputsUpdated);
      }
    }

    setIsAddVendorOpen(false);
  };

  return <LmsFormGenerator myForm={myForm} actions={formActions} handleSubmit={handleSubmit} handleClose={() => setIsAddVendorOpen(false)} />;
};

const AddDrawDetailsForm = (
  {
    loanDrawTempData,
    setLoanDrawTempData = undefined as any,
    isLoading = false,
    error = undefined as any,
    setError = undefined as any,
    isOriginalDraw = false,
    isNewDraw = false,
    directPay: directPayGlobal = false,
    loanPropertyId = "",
    drawId = "",
    handleClose = undefined as any,
  }) => {
  const [isDrawInspectionOpen, setIsDrawInspectionOpen] = useState(false);
  const [voidValues, setVoidValues] = useState(undefined as any);
  const [isVoidFormModalOpen, setIsVoidFormModalOpen] = useState(undefined as any);
  const [{ fetchData }] = useFetchData();
  const [inputsCopy, setInputsCopy] = useState<Record<string, any> | undefined>();
  const loanDrawTempDetails = loanDrawTempData?.loanDrawTempDetails || [];
  const loanDrawTempDetailsLength = loanDrawTempDetails?.length || 0;
  const [loanComments, setLoanComments] = useState(undefined as any);

  useEffect(() => {
    if (loanDrawTempData?.loanComments) {
      setLoanComments(loanDrawTempData?.loanComments);
    }
  }, [loanDrawTempData?.loanComments]);

  const handleRemoveVendor = (options) => {
    if (options.handleConfirmFunc) {
      options.handleConfirmFunc(() => {
          const { inputs, nestedInputsProp, removeInput, setConfirmOptions } = options;
          const loanBudgetDetailId = loanDrawTempDetails && loanDrawTempDetails[nestedInputsProp] ? loanDrawTempDetails[nestedInputsProp].loanBudgetDetailId : null;
          if (loanBudgetDetailId) {
            const vendorsTotal = loanDrawTempDetails.reduce((total: any, currentRow: any) => {
              if (currentRow && currentRow.loanBudgetDetailId === loanBudgetDetailId) {
                return total + 1;
              }

              return total;
            }, 0);
            if (vendorsTotal === 1) {
              removeInput(options.name, options.nestedInputsProp);
              setConfirmOptions({});
            }
            else {
              const loanDrawTempDataDetailsUpdated = [...loanDrawTempDetails];
              loanDrawTempDataDetailsUpdated.splice(nestedInputsProp, 1);
              const { inputsUpdated } = getArrayFromInputsWithProps(inputs, undefined, nestedInputsProp);
              if (setLoanDrawTempData) {
                setLoanDrawTempData({ ...loanDrawTempData, loanDrawTempDetails: loanDrawTempDataDetailsUpdated });
              }
              setInputsCopy(inputsUpdated);
              setConfirmOptions({});
            }
          }
        }, "Are you sure you want to remove this vendor?");
    }
  };

  const handleVoidDrawClick = (options) => {
    const confirmVoidPayload = {
      loanDrawId: drawId,
      inspectionFee: options?.inputs?.inspectionFee,
      dateDownFee: options?.inputs?.dateDownFee
    };

    options.handleConfirmFunc(async () => {
      const { data: resData, error: resError } = await fetchData("draws/confirm-void-draw", {
        fetchMethod: "POST",
        data: confirmVoidPayload
      });

      if (resError && setError) {
        setError(resError);
      }
      else {
        if (resData.confirmMessage) {
          if (confirm(resData.confirmMessage)) {
            setVoidValues({ ...confirmVoidPayload, loanDrawTempId: drawId });
            setIsVoidFormModalOpen(true);
          }
        }
        else {
          setVoidValues({ ...confirmVoidPayload, loanDrawTempId: drawId });
          setIsVoidFormModalOpen(true);
        }
      }

      options.setConfirmOptions({});
    }, "Are you sure you want to Void this Draw?");
  };

  const myForm = new BCFormController(inputsCopy || loanDrawTempData, { isCompact: true, styles: { minWidth: "2200px" } });
  if (isNewDraw) {
    myForm.addCenteredGroup([
      myForm.getButtonField("Void Draw", handleVoidDrawClick, {
        width: 2,
        position: "center",
        buttonIcon: GlobalConstants.Icons.CARET_UP,
        fullWidth: true
      })
    ], { blankWidth: 7 });
  }

  if (loanDrawTempDetails) {
    myForm.addGroupFields([
      myForm.getLabelField("Budget Category", { width: 3, position: "center" }),
      myForm.getLabelField("Sub-Category", { width: 2, position: "center" }),
      myForm.getLabelField("Direct Pay", { width: 1, position: "center" }),
      myForm.getLabelField("Vendor", { width: 3, position: "center" }),
      myForm.getLabelField("Check if Paid", { width: 1, position: "center" }),
      myForm.getLabelField("Paid By", { width: 1, position: "center" }),
      myForm.getLabelField("Budget Amount", { width: 2, position: "center" }),
      myForm.getLabelField("Disbursed", { width: 2, position: "center" }),
      myForm.getLabelField("Budget Remaining", { width: 2, position: "center" }),
      myForm.getLabelField("Available (Previously Inspected)", { width: 2, wrapLabel: true, position: "center" }),
      myForm.getLabelField("Requested Amount", { width: 2, position: "center" }),
      myForm.getLabelField("Invoice #/ Reference", { width: 2, position: "center" }),
      myForm.getLabelField("Comments", { width: 2, position: "center" }),
    ], { widths: 1 });

    if (loanDrawTempDetailsLength > 0) {
      loanDrawTempDetails?.map((rowData, i) => {
        const {
          isSelected,
          loanBudgetCategoryDisplay,
          loanBudgetSubCategoryDisplay,
          paymentMethodOption, // TODO: Is missing
          directPay,
          isPaid,
          budgetAmount,
          availableWithoutInspection,
          drawRequestedAmount,
          drawReference,
          disbursedAmount,
          description,
          payeeOrganizationDisplay
        } = rowData;

        const budgetRemaining = isNaN(budgetAmount - disbursedAmount) ? 0 : (budgetAmount - disbursedAmount).toFixed(2);
        const availablePreviouslyInspectedFieldName = `availablePreviouslyInspected-${i}`;

        myForm.addGroupFields([
          myForm.getCheckboxField("", { checkboxText: loanBudgetCategoryDisplay, name: "isSelected", value: isSelected, width: 3, inlineRight: true, nestedInputsProp: i }),
          myForm.getInputField("Sub Category", { label: "", value: loanBudgetSubCategoryDisplay, width: 2, disabled: true, nestedInputsProp: i }),
          myForm.getCheckboxField("Direct Pay", { label: "", width: 1, value: directPayGlobal || directPay, nestedInputsProp: i }),
          myForm.getDropdownField("", "draws/payee-organizations", {
            name: "payeeOrganizationId",
            modalContent: (setIsAddFormModalOpen, options) => (
              <AddVendorToAddDrawForm
                setIsAddVendorOpen={setIsAddFormModalOpen}
                options={options}
                loanDrawTempData={loanDrawTempData}
                setLoanDrawTempData={setLoanDrawTempData}
                setInputsCopy={setInputsCopy}
              />
            ),
            dropdownListKey: "name",
            actionIcon: GlobalConstants.Icons.ADD,
            removeActionFunc: handleRemoveVendor,
            removeActionIcon: GlobalConstants.Icons.DELETE,
            placeHolder: " ",
            value: payeeOrganizationDisplay,
            disabled: true,
            isActionEnabled: true,
            width: 3,
            isHidden: { directPay: true },
            isHiddenOperation: "notequal",
            keepHiddenPosition: true,
            nestedInputsProp: i,
            calculatedTargetField: "vendorTotalDisplayed",
            noBackground: true
          }),
          myForm.getCheckboxField("Check if Paid", {
            label: "",
            name: "isPaid",
            value: isPaid,
            isDisabledOn: { directPay: true },
            isDisabledOnOperation: "notequal",
            width: 1,
            nestedInputsProp: i
          }),
          myForm.getInputField("Paid By", { label: "", value: paymentMethodOption, disabled: true, width: 1, nestedInputsProp: i }),
          myForm.getCurrencyField("Budget Amount", { label: "", value: budgetAmount, width: 2, disabled: true, nestedInputsProp: i }),
          myForm.getCurrencyField("Disbursed", { label: "", value: disbursedAmount, width: 2, disabled: true, nestedInputsProp: i }),
          myForm.getCurrencyField("Budget Remaining", { label: "", value: budgetRemaining, width: 2, disabled: true, nestedInputsProp: i }),
          myForm.getCurrencyField("Available (Previously Inspected)", {
            label: "",
            name: availablePreviouslyInspectedFieldName,
            value: availableWithoutInspection,
            width: 2,
            disabled: true,
            nestedInputsProp: i
          }),
          myForm.getCurrencyField("Requested Amount", { label: "", value: drawRequestedAmount, width: 2, nestedInputsProp: i, calculatedTargetField: "requestedAmountTotal" }),
          myForm.getInputField("Invoice #/ Reference", { label: "", value: drawReference, width: 2, nestedInputsProp: i }),
          myForm.getInputField("Comments", { label: "", value: description, width: 2, nestedInputsProp: i })
        ], { widths: 1 });
      });
    }

    const calculateVendorsTotal = (inputs) => {
      let total = 0;
      for (let i = 0; i < loanDrawTempData?.loanDrawTempDetails?.length; i++) {
        if (inputs[i]?.payeeOrganizationId) {
          total++;
        }
      }

      return total;
    };

    myForm.addGroupFields([
      myForm.getBlankField({ width: 2 }),
      myForm.getBlankField({ width: 2 }),
      myForm.getBlankField({ width: 1 }),
      myForm.getLabelField("Vendor Total", { position: "center", width: 1 }),
      myForm.getNumberField("Vendor Total Displayed", { label: "", calculatedFunc: calculateVendorsTotal, width: 2, disabled: true }),
      myForm.getBlankField({ width: 2 }),
      myForm.getBlankField({ width: 1 }),
      myForm.getBlankField({ width: 2 }),
      myForm.getBlankField({ width: 2 }),
      myForm.getBlankField({ width: 3 }),
      myForm.getLabelField("Totals", { position: "center", width: 1 }),
      myForm.getCurrencyField("Requested Amount Total", { label: "", width: 2 }),
      myForm.getBlankField({ width: 2 }),
      myForm.getBlankField({ width: 2 }),
    ], { widths: 1 });
  }

  myForm.addDividerField();

  if (isOriginalDraw || isNewDraw) {
    myForm.addGroupFields([
      myForm.getButtonField("Order Date Down", undefined, {
        name: "",
        alignRight: true,
        buttonIcon: GlobalConstants.Icons.CARET_UP,
        disabled: true
      }),
      myForm.getCalendarField("Order Date Down", { label: "", name: "orderDateDownDate", width: 4 }),
      myForm.getButtonField("Draw Inspection", () => setIsDrawInspectionOpen(true), { alignRight: true, buttonIcon: GlobalConstants.Icons.CARET_UP }),
      myForm.getBlankField({ width: 4 }),
      myForm.getBlankField(),
    ]);
    myForm.addGroupFields([
      myForm.getButtonField("Wave Date Down", undefined, { alignRight: true, buttonIcon: GlobalConstants.Icons.CARET_UP, disabled: true }),
      myForm.getCalendarField("Wave Date Down", { label: "", width: 4 }),
      myForm.getButtonField("Wave Date Inspection", undefined, {
        alignRight: true,
        buttonIcon: GlobalConstants.Icons.CARET_UP,
        disabled: true
      }),
      myForm.getCalendarField("Wave Date Inspection", { label: "", width: 4 }),
      myForm.getBlankField(),
    ]);
    myForm.addGroupFields([
      myForm.getLabelField("Date Down Received", { position: "right-center" }),
      myForm.getCalendarField("Date Down Received Date", { label: "", width: 4 }),
      myForm.getLabelField("Inspection Received", { position: "right-center" }),
      myForm.getCalendarField("Inspection Received Date", { label: "", width: 4 }),
      myForm.getBlankField(),
    ]);
    myForm.addGroupFields([
      myForm.getBlankField(),
      myForm.getTextareaField("Date Down Received Comment", { label: "", width: 4 }),
      myForm.getBlankField(),
      myForm.getTextareaField("Inspection Received Comment", { label: "", width: 4 }),
      myForm.getDisplayField("Title", { name: "titleNumber" })
    ]);
    myForm.addCenteredGroup([
      myForm.getBlankField({ width: 6 }),
      myForm.getComponent(<LmsTable apiSuffix={`draws/${drawId}/work-inspections`} isDisabled key={`draws/${drawId}/work-inspections`} />, { width: 6 }),
    ], { blankWidth: 2 });
    setInspectionSectionFields(myForm);
    myForm.addDividerField();

    if (loanComments?.length > 0) {
      myForm.addGroupFields([
        myForm.getLabelField("Subject", { width: 2, position: "center" }),
        myForm.getLabelField("Date", { width: 2, position: "center" }),
        myForm.getLabelField("Comments", { width: 2, position: "center" }),
      ], { widths: 3 });
      loanComments.forEach((comment, index) => {
        myForm.addGroupFields([
          myForm.getInputField("Subject", { label: "", name: "commentSubject", width: 2, value: comment.commentSubject, nestedInputsProp: `comment-${index}`, disabled: true }),
          myForm.getCalendarField("Date", { label: "", name: "commentDate", width: 1, value: comment.commentedOn, nestedInputsProp: `comment-${index}`, disabled: true }),
          myForm.getTextareaField("Comments", { label: "", width: 3, value: comment.description, nestedInputsProp: `comment-${index}`, disabled: true }),
        ], { widths: 3 });
      });
    }
  }

  useEffect(() => {
    if (loanDrawTempData) {
      setInputsCopy({ ...myForm.values, ...inputsCopy });
    }
  }, [loanDrawTempDetailsLength]);

  const onFormChanged = async (options, lastUpdated, nestedProp) => {
    const inputs = options?.inputs || {};
    if (lastUpdated === "requestedAmount" && inputs[nestedProp]) {
      const requestedAmount = inputs[nestedProp].requestedAmount;
      const requestedAmountNum = inputs[nestedProp].requestedAmount ? parseFloat(inputs[nestedProp].requestedAmount) : 0;
      const budgetRemaining = inputs[nestedProp].budgetRemaining;
      const budgetRemainingNum = inputs[nestedProp].budgetRemaining ? parseFloat(inputs[nestedProp].budgetRemaining) : 0;
      if (requestedAmount) {
        if (!inputs[nestedProp]?.isSelected) {
          options.updateInput("isSelected", true, nestedProp);
        }

        if (budgetRemainingNum < requestedAmountNum) {
          options.updateInput("requestedAmount", inputs[nestedProp].budgetRemaining, nestedProp);
          options.setFormErrorMessage({ message: `Draw Requested Amount ${requestedAmount} cannot be greater than Available Amount ${budgetRemaining}` });
        }
      }
    }

    if (lastUpdated === "isSelected" && inputs[nestedProp]) {
      if (inputs[nestedProp].isSelected) {
        inputs[nestedProp].requestedAmount = "0.00";
      }
      else {
        inputs[nestedProp].requestedAmount = "";
      }
    }

    if (lastUpdated === "directPay") {
      if (inputs[nestedProp] && !inputs[nestedProp].directPay && inputs[nestedProp].payeeOrganizationDisplay && !options.isConfirmOpen) {
        options.setIsConfirmOpen(true);
        options.setOnConfirmFunc({
          func: () => {
            options.setInputs({ ...inputs, [nestedProp]: { ...inputs[nestedProp], payeeOrganizationDisplay: null } });
          },
          message: "Are you sure you want to UNCHECK 'Direct Pay'? Vendor will be removed from the line item."
        });

        options.setInputs({ ...inputs, [nestedProp]: { ...inputs[nestedProp], directPay: true } });
      }
    }
  };

  const getUpdatedTempData = (inputs) => {
    const { updatedArr } = getArrayFromInputsWithProps(inputs, loanDrawTempData?.loanDrawTempDetails);
    return {
      ...loanDrawTempData,
      loanDrawTempDetails: updatedArr
    };
  };

  const handleSave = async (inputs, options) => {
    const updatedTempData = getUpdatedTempData(inputs);
    if (hasSelectedRow(updatedTempData?.loanDrawTempDetails)) {
      const useFormInstance = options.useFormInstance;
      useFormInstance.initSubmittingStates(options.buttonText);

      const validateDrawPayload = {
        loanDrawTempId: loanDrawTempData?.id,
        orderDateDownDate: new Date().toLocaleDateString(),
        totalDrawAmount: inputs?.requestedAmountTotal,
        validateInspection: false
      };

      const { data, error } = await fetchData("draws/validate-save", { fetchMethod: "POST", data: validateDrawPayload });

      if (!error && data) {
        if (!data.existsCommitmentCap) {
          options.handleSubmit("draws/save", { method: "PUT", data: updatedTempData, buttonText: options.buttonText });
        }
        else {
          useFormInstance.resetSubmittingStates();
        }
      }
    }
    else {
      options.setFormErrorMessage({ message: "Please select a line item." });
    }
  };

  const handleSubmitRequest = async (inputs, handleSubmit, useFormInstance) => {
    const updatedTempData = getUpdatedTempData(inputs);
    if (hasSelectedRow(updatedTempData?.loanDrawTempDetails)) {
      useFormInstance.initSubmittingStates(useFormInstance.buttonText);

      const validateDrawPayload = {
        loanDrawTempId: loanDrawTempData?.id,
        orderDateDownDate: new Date().toLocaleDateString(),
        totalDrawAmount: inputs?.requestedAmountTotal,
        validateInspection: false
      };
      const { data, error } = await fetchData("draws/validate-submit", { fetchMethod: "POST", data: validateDrawPayload });
      if (data && !data.existsCommitmentCap) {
        handleSubmit("draws/submit", { method: "PUT", data: updatedTempData, buttonText: useFormInstance.buttonText });
      }
      else {
        if (error) {
          useFormInstance.setFormErrorMessage(error);
        }
        useFormInstance.resetSubmittingStates();
      }  
    }
    else {
      useFormInstance.setFormErrorMessage({ message: "Please select a line item." });
    }
  };

  const handleRefreshRequest = async (inputs, handleSubmit, useFormInstance) => {
    useFormInstance.initSubmittingStates(useFormInstance.buttonText);
    handleSubmit("draws/refresh-temp", { method: "PUT", data: getUpdatedTempData(inputs), buttonText: useFormInstance.buttonText });
  };

  const formActions = [
    { text: "Modify Budget", path: `/servicing-modify-budgets/edit/${loanPropertyId}` },
    { text: "Refresh", handleClick: handleRefreshRequest, icon: GlobalConstants.Icons.SAVE },
    "save",
    "save-close",
    { text: "Submit Request", handleClick: handleSubmitRequest, icon: GlobalConstants.Icons.CARET_UP },
    "close"
  ];

  return <>
    {loanDrawTempDetailsLength > 0 && <LmsFormGenerator
      myForm={myForm}
      actions={formActions}
      inputs={inputsCopy}
      isLoading={isLoading}
      error={error}
      handleSubmit={handleSave}
      onFormChanged={onFormChanged}
      handleClose={handleClose}
      skipActions={isOriginalDraw && !isNewDraw}
    />}
    {isDrawInspectionOpen && <BCModal
      content={<LmsCompanyInspectionsTable suffix={`draws/${drawId}/inspections`} />}
      isModalOpen={isDrawInspectionOpen}
      onClose={() => setIsDrawInspectionOpen(false)}
      size="large"
      closeIcon={true}
    />}
    <BCModal
      isModalOpen={isVoidFormModalOpen}
      onClose={() => setIsVoidFormModalOpen(false)}
      closeIcon
      content={<VoidReasonForm values={voidValues} setIsVoidFormModalOpen={setIsVoidFormModalOpen} />}
    />
  </>;
};

const AddDrawForm = ({ loanId = "" }) => {
  const [{ data, fetchData, isLoading }] = useFetchData(loanId ? `loans/${loanId}` : "");
  const [loanDrawTempData, setLoanDrawTempData] = useState<Record<string, any> | undefined>();
  const [inputsCopy, setInputsCopy] = useState<Record<string, any> | undefined>();
  const loanDrawTempDetails = loanDrawTempData?.loanDrawTempDetails;
  const loanDrawTempDetailsLength = loanDrawTempDetails?.length || 0;

  const initialData = {
    borrowerOrganizationId: data?.borrowerOrganizationId,
    loanId
  };

  const styles = loanDrawTempDetailsLength > 0 ? {} : { minHeight: "400px" };
  const myForm = new BCFormController(inputsCopy || initialData, { isCompact: true, initIsDisabled: loanDrawTempDetailsLength > 0, styles: styles });
  myForm.addGroupFields([
    myForm.getDropdownField("Borrower", "metadata/list-borrower-organizations", {
      name: "borrowerOrganizationId",
      disabled: !!loanId || loanDrawTempDetailsLength > 0,
      required: true
    }),
    myForm.getDropdownField("Loan", "loans/list-borrower-loans", {
      optionalDropdownQueries: [{ query: "borrowerOrganizationId", fieldName: "borrowerOrganizationId" }],
      disabled: !!loanId || loanDrawTempDetailsLength > 0,
      required: true
    }),
    myForm.getDropdownField("Loan Property", "loans/loan-properties", { dependencyField: "loanId", disabled: loanDrawTempDetailsLength > 0 }),
    myForm.getCheckboxField("Direct Pay", { disabled: true })
  ]);
  myForm.addGroupFields([myForm.getTextareaField("Description", { width: 8 })], { widths: 8 });

  const onFormChanged = async (options, lastUpdated) => {
    const inputs = options?.inputs || {};

    if (lastUpdated === "loanId") {
      const loanId = inputs?.loanId;
      const { data } = await fetchData(`draws/loan/${loanId}/details`);
      if (data) {
        const inputsCopy = { ...inputs };
        if (data?.directPay !== undefined) {
          inputsCopy.directPay = data?.directPay;
        }

        if (data?.borrowerOrganizationId !== undefined) {
          inputsCopy.borrowerOrganizationId = data?.borrowerOrganizationId;
        }

        setInputsCopy(inputsCopy);
      }
    }

    if (loanDrawTempDetailsLength === 0 && inputs?.loanId && inputs?.borrowerOrganizationId && inputs.loanPropertyId && lastUpdated === "loanPropertyId") {
      const id = guid();
      const postData = {
        borrowerOrganizationId: inputs?.borrowerOrganizationId,
        loanId: inputs?.loanId,
        loanPropertyId: inputs?.loanPropertyId,
        id: id
      };

      options.setIsFormLoading(true);
      const res = await fetchData("draws/loan-draw-temp", { fetchMethod: "POST", data: postData, useMockData: false });

      if (!res.error) {
        const { data } = await fetchData(`draws/loan-draw-temp/${id}`, { fetchMethod: "GET", useMockData: false });
        setInputsCopy({ ...inputs });
        setLoanDrawTempData(data);
        options.setIsFormLoading(false);
      }
      else {
        options.setIsFormLoading(false);
      }
    }
  };

  return <>
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      inputs={inputsCopy}
      onFormChanged={onFormChanged}
      actions={["close"]}
      skipActions={!!loanDrawTempData}
    />
    <AddDrawDetailsForm
      loanDrawTempData={loanDrawTempData}
      setLoanDrawTempData={setLoanDrawTempData}
      directPay={!!inputsCopy?.directPay}
      loanPropertyId={inputsCopy?.loanPropertyId}
    />
    {loanDrawTempData && inputsCopy?.loanId && <AddDrawTabPages loanId={inputsCopy?.loanId} loanDrawTempId={loanDrawTempData?.id} />}
  </>;
};

const addViewDrawFormFields = (myForm) => {
  if (myForm?.values?.workInspectionPending && myForm?.values?.workInspectionPendingCreatedOn === null) {
    myForm.addCenteredGroup([myForm?.getWarningTextField("Work Inspection Pending", { position: "center" })]);
  }
  myForm.addGroupFields([
    myForm.getDisplayField("Draw Name", { name: "name" }),
    myForm.getDisplayField("Loan", { name: "loanDisplay" }),
    myForm.getDisplayField("Date Down Status", { name: "dateDownDrawRequestStatus" }),
    myForm.getCalendarField("Date Down Received", { name: "dateDownReceivedDate", disabled: true }),
  ]);
  myForm.addGroupFields([
    myForm.getDisplayField("Borrower", { name: "borrowerOrganizationName" }),
    myForm.getDisplayField("Loan Property", { name: "loanPropertyDisplay" }),
    myForm.getDisplayField("Work Inspection Status", { name: "workInspectorDrawRequestStatus" }),
    myForm.getCalendarField("Inspection Received", { name: "inspectionReceivedDate", disabled: true }),
  ]);

  myForm.addGroupFields([
    myForm.getCurrencyField("Paid At Funding"),
    myForm.getCurrencyField("Current Amount Pending"),
    myForm.getBlankField(),
    myForm.getBlankField()
  ], { isHiddenFunc: ({ inputs }) => inputs.loanDrawStatusDisplay !== "Funded" });

  myForm.addGroupFields([
    myForm.getTextareaField("Description"),
    myForm.getTextareaField("Send To Borrower Comment"),
  ]);
  myForm.addGroupFields([
    myForm.getInputField("Title #", { name: "titleNumber" }),
    myForm.getBlankField(),
    myForm.getBlankField(),
    myForm.getBlankField(),
  ]);
  return myForm;
};

const EditFormTabTables = ({ id, loanId = "" }) => {
  const tabPages = {
    Details: <LmsLoanDrawDetailsTable suffix={`draws/detail-summary/${id}`} />,
    Documents: <LmsTable 
      apiSuffix={`draws/document/${id}`} 
      actionButtons={["export"]} 
      hideProps={["contact"]}
      columnLabelsDictionary={{
        includeInLrcpolling: "Include In LRC Polling"
      }}
      columnClickOptions={{
        documentFileName: {
          idProp: "id",
          downloadPath: "document/download",
        }
      }}
    />,
    "Inspection Company Messages": <LmsInspectionCompanyMessagesTable 
      suffix={`draws/inspection-messages/${id}`} 
      loanId={loanId} 
      loanDrawId={id} 
    />,
    Comments: <LmsTable 
      apiSuffix={`draws/comments/${id}`} 
      actionButtons={[{ text: "Add", path: `/loandetails/addcomment/?loanId=${loanId}&loanDrawId=${id}` }, "export"]} 
      skipClick 
      columnLabelsDictionary={{
        loanNumber: "Loan #",
        applicationNumber: "Application #",
        name: "Project Name",
        description: "Comments"
      }}
      hideProps={["isEmailed", "isExternal"]}
    />,
    Vendors: <LmsTable apiSuffix={`draws/ach/${id}`} actionButtons={["export"]} />,
    History: <LmsTable apiSuffix={`draws/history/${id}`} actionButtons={["export"]} />
  };

  return <LmsTabPages tabPages={tabPages} />;
};

const RequestedFormTabTables = ({ id, loanId = "", newCommentId = "", setNewCommentId }) => {
  const commentsActionButtons = [{
    text: "Add",
    action: GlobalConstants.TableDefaultActions.MODAL_BUTTON,
    modalContent: (setIsAddFormModalOpen) => (
      <AddLoanDetailsCommentModal
        loanId={loanId}
        loanDrawId={id}
        setIsModalOpen={setIsAddFormModalOpen}
        setNewCommentId={setNewCommentId}
      />
    ),
    isDisabled: () => false
  }];

  const tabPages = {
    Comments: <LmsTable
      apiSuffix={`draws/comments/${id}`}
      actionButtons={commentsActionButtons}
      skipClick
      columnLabelsDictionary={{
        loanNumber: "Loan #",
        applicationNumber: "Application #",
        name: "Project Name",
        description: "Comments"
      }}
      hideProps={["isEmailed", "isExternal"]}
      refreshDataDependency={newCommentId}
    />,
    Documents: <LmsTable
      apiSuffix={`draws/document/${id}`}
      actionButtons={["export"]}
      hideProps={["contact"]}
      columnLabelsDictionary={{
        includeInLrcpolling: "Include In LRC Polling"
      }}
      columnClickOptions={{
        documentFileName: {
          idProp: "id",
          downloadPath: "document/download",
        }
      }}
    />,
    "Inspection Company Messages": <LmsInspectionCompanyMessagesTable
      suffix={`draws/inspection-messages/${id}`}
      loanId={loanId}
      loanDrawId={id}
    />,
    History: <LmsTable apiSuffix={`draws/history/${id}`} actionButtons={["export"]} />
  };

  return <LmsTabPages tabPages={tabPages} refreshDependency={newCommentId} />;
};

const AddPayeeForm = ({ setIsAddFormModalOpen, options }) => {
  const onSubmitSuccess = (responseData) => {
    if (responseData?.id || isGuid(responseData)) {
      options.handleFormChange(undefined, { value: responseData?.id || responseData, name: "payeeOrganizationId" }, true);
    }

    setIsAddFormModalOpen(false);
  };

  const myForm = addOrganizationFormFields(new BCFormController({ organizationTypeId: GlobalConstants.OrganizationTypes.Vendor }, {
    submitSuffix: LmsEndpoints.Orgs.POST.ORG,
    payloadDataType: "FormData",
    onSubmitSuccess,
    metaData: { hasContactFirstLastName: true }
  }), null, true, true);

  const formActions = ["save-close", "close"];

  return <LmsFormGenerator myForm={myForm} actions={formActions} handleClose={() => setIsAddFormModalOpen(false)} displayMessageLocally />;
};

const setAddVendorFields = (myForm) => {
  myForm.addCenteredGroup([myForm.getInputField("Paid Vendor Name")]);
  myForm.addCenteredGroup([myForm.getDropdownField("Vendor", "draws/payee-organizations", {
    name: "payeeOrganizationId",
    modalContent: (setIsAddFormModalOpen, options) => <AddPayeeForm setIsAddFormModalOpen={setIsAddFormModalOpen} options={options} />,
    modalSize: "fullscreen",
    actionIcon: GlobalConstants.Icons.CARET_UP,
    actionLabel: "Add Payee"
  })]);

  return myForm;
};

const setInspectionSectionFields = (myForm) => {
  myForm.addGroupFields([
    myForm.getBlankField(),
    myForm.getCheckboxField("Meet Inspector Onsite", { width: 2, inlineRight: true, disabled: true }),
    myForm.getTextareaField("Meet Inspector Onsite Comment", { name: "meetInspectorOnsiteDescription", label: "", width: 6, disabled: true }),
  ], { widths: 1 });
  myForm.addGroupFields([
    myForm.getBlankField(),
    myForm.getCheckboxField("Provide More Detail", { width: 2, inlineRight: true, disabled: true }),
    myForm.getTextareaField("Provide More Detail Comment", { name: "provideMoreDetailDescription", label: "", width: 6, disabled: true }),
  ], { widths: 1 });
  myForm.addGroupFields([
    myForm.getBlankField(),
    myForm.getCheckboxField("Inspection Not Needed", { width: 2, inlineRight: true, disabled: true }),
    myForm.getTextareaField("Inspection Not Needed Comment", { name: "inspectionNotNeededDescription", label: "", width: 6, disabled: true }),
  ], { widths: 1 });
};

const LmsCompanyInspectionsTable = ({ suffix = "" }) => {
  const [isAddInspectionVisible, setIsAddInspectionVisible] = useState(false);
  const [editInspectionId, setEditInspectionId] = useState("");
  const actionButtons = [
    { action: "add", handleClick: () => setIsAddInspectionVisible(true), icon: GlobalConstants.Icons.ADD },
    { action: "edit", handleClick: (options) => setEditInspectionId(options?.firstCheckedRow?.id) },
    "remove",
    "export",
    "save",
    "cancel"
  ];

  return <>
    <LmsTable actionButtons={actionButtons} apiSuffix={suffix} />
    <BCModal
      isModalOpen={isAddInspectionVisible}
      size="large"
      closeIcon
      onClose={() => setIsAddInspectionVisible(false)}
      content={<AddInspectionCompanyInspectionsForm handleClose={() => setIsAddInspectionVisible(false)} />}
    />
    {editInspectionId && <BCModal
      isModalOpen={true}
      size="large"
      closeIcon
      onClose={() => setEditInspectionId("")}
      content={<EditInspectionCompanyInspectionsForm id={editInspectionId} handleClose={() => setEditInspectionId("")} />}
    />}
  </>;
};

const ViewOriginalDraw = ({ id, setIsModalOpen = undefined as any, isNewDraw = false }) => {
  const [{ data, isLoading, error }] = useFetchData(`draws/${id}/original-draw-request`);
  const [loanDrawTempData, setLoanDrawTempData] = useState(null);
  const [isDrawInspectionOpen, setIsDrawInspectionOpen] = useState(false);
  const [newCommentId, setNewCommentId] = useState("");
  const commentsActionButtons = [{
    text: "Add",
    action: GlobalConstants.TableDefaultActions.MODAL_BUTTON,
    modalContent: (setIsAddFormModalOpen) => (
      <AddLoanDetailsCommentModal
        loanId={data?.loanId}
        loanDrawId={id}
        setIsModalOpen={setIsAddFormModalOpen}
        setNewCommentId={setNewCommentId}
      />
    ),
    isDisabled: () => false
  }];

  useEffect(() => {
    if (data?.loanDrawTemp && !loanDrawTempData) {
      setLoanDrawTempData(data.loanDrawTemp);
    }
  }, [data]);

  const loanInfoHeaderForm = new BCFormController(data, { isCompact: true, initIsDisabled: true });
  loanInfoHeaderForm.addGroupFields([
    loanInfoHeaderForm.getDropdownDisplayField("Borrower", { name: "borrowerOrganizationName" }),
    loanInfoHeaderForm.getDropdownDisplayField("Loan", { name: "loanDisplay" }),
    loanInfoHeaderForm.getDropdownDisplayField("Loan Property", { name: "loanPropertyDisplay", disabled: true }),
    loanInfoHeaderForm.getCheckboxField("Direct Pay")
  ]);
  loanInfoHeaderForm.addGroupFields([loanInfoHeaderForm.getTextareaField("Description", { width: 8 })], { widths: 8 });

  const tabPages = {
    Comments: <LmsTable 
      apiSuffix={`draws/comments/${id}`} 
      actionButtons={commentsActionButtons} 
      refreshDataDependency={newCommentId} 
      skipClick 
    />,
    History: <HistoryTable suffix={`draws/history/${id}`} />,
  };

  const handleClose = setIsModalOpen ? () => setIsModalOpen(false) : undefined;
  return <>
    <LmsFormGenerator myForm={loanInfoHeaderForm} skipActions isLoading={isLoading} />
    <AddDrawDetailsForm
      loanDrawTempData={loanDrawTempData}
      setLoanDrawTempData={setLoanDrawTempData}
      isOriginalDraw
      isNewDraw={isNewDraw}
      isLoading={isLoading}
      error={error}
      drawId={id}
      handleClose={handleClose}
    />

    {loanDrawTempData && <LmsTabPages tabPages={tabPages} refreshDependency={newCommentId} />}
    {isDrawInspectionOpen && <BCModal
      content={<LmsCompanyInspectionsTable suffix={`draws/${id}/inspections`} />}
      isModalOpen={isDrawInspectionOpen}
      onClose={() => setIsDrawInspectionOpen(false)}
      size="large"
      closeIcon={true}
    />}
  </>;
};

const AddDrawRequestedDetailsForm = (
  {
    id,
    drawData,
    setNewCommentId
  }) => {
  const [{ data: drawDetails, isLoading, error, setData, setError, fetchData }] = useFetchData(`draws/${id}/info`);
  const loanDrawBudgetDetails = drawDetails?.loanDrawTempBudgetDetails;
  const loanDrawTempId = drawDetails?.id || "";
  const loanPropertyId = drawDetails?.loanPropertyId || "";
  const bankInformationId = drawDetails?.bankInformationId || "";
  const [loanDrawComments, setLoanDrawComments] = useState<Record<string, any>[]>([] as any[]);
  const [IsDrawExceedsPromptOpen, setIsDrawExceedsPromptOpen] = useState(false);
  const [voidValues, setVoidValues] = useState(undefined as any);
  const [isVoidFormModalOpen, setIsVoidFormModalOpen] = useState(undefined as any);
  const isRequiresBorrowerRevision = drawData?.loanDrawStatusDisplay === "Requires Borrower Revision";

  useEffect(() => {
    if (drawDetails?.loanComments) {
      setLoanDrawComments(drawDetails?.loanComments);
    }
  }, [drawDetails]);
  const handleRefresh = async (options) => {
    if (options?.initSubmittingStates) {
      options?.initSubmittingStates(options.buttonText);
    }

    const { error } = await fetchData("draws/refresh-temp", { fetchMethod: "PUT", data: getUpdatedTempData(drawDetails, options) });
    if (error) {
      options.setFormErrorMessage(error);
    }

    safelyRunFunction(options?.resetSubmittingStates);
  };

  const handleApprove = async (options) => {
    const useFormInstance = options;
    if (useFormInstance?.initSubmittingStates) {
      useFormInstance?.initSubmittingStates(options.buttonText);
    }

    const validateDrawPayload = {
      loanDrawTempId: drawDetails?.id,
      orderDateDownDate: new Date().toLocaleDateString(),
      totalDrawAmount: options?.inputs?.requestedAmountTotal,
      budgetDetailIdWithApprovedAmount: getBudgetDetailsIdsWithApprovedAmount(options?.inputs),
    };

    const { data, error } = await fetchData("draws/validate-approve", { fetchMethod: "POST", data: validateDrawPayload });
    if (error) {
      useFormInstance.setFormErrorMessage(error);
      safelyRunFunction(useFormInstance?.resetSubmittingStates);
    }

    if (!error && data) {
      if (!data.existsCommitmentCap) {
        options.handleSubmit("draws/approve", { method: "PUT", data: getUpdatedTempData(drawDetails, options), buttonText: options.buttonText });
      }
      else {
        safelyRunFunction(useFormInstance?.resetSubmittingStates);
        setIsDrawExceedsPromptOpen(true);
      }
    }
  };

  const handleSendBackToBorrower = async (options, submitSuffix =  "draws/send-back-to-borrower") => {
    if (options?.initSubmittingStates) {
      options?.initSubmittingStates(options.buttonText);
    }

    const validateDrawPayload = {
      loanDrawTempId: drawDetails?.id,
      orderDateDownDate: new Date().toLocaleDateString(),
      totalDrawAmount: options?.inputs?.requestedAmountTotal
    };

    const { data, error } = await fetchData("draws/validate", { fetchMethod: "POST", data: validateDrawPayload });
    if (error) {
      options.setFormErrorMessage(error);
      safelyRunFunction(options?.resetSubmittingStates);
    }
    else {
      if (!data.existsCommitmentCap) {
        options.handleSubmit(submitSuffix, { method: "PUT", data: getUpdatedTempData(drawDetails, options), buttonText: options.buttonText });
      }
      else {
        safelyRunFunction(options?.resetSubmittingStates);
        setIsDrawExceedsPromptOpen(true);
      }
    }
  };

  const formatBankDataFunc = (data) => {
    if (data?.bankInfoData) {
      return data?.bankInfoData;
    }

    return [];
  };

  const getUpdatedVendorRowDetails = (inputs, index, vendorDetailsCopy = [] as any[], setInputs, addedVendor = {} as IAddedVendorData, editedVendorId = "") => {
    const remainingVendorDetails = [...vendorDetailsCopy];
    const updatedVendorDetails = [] as any[];
    const { payeeOrganizationId, payeeOrganizationDisplay, detailsIndex } = addedVendor;
    Object.entries(inputs).forEach(([key, value]: [any, any]) => {
      if (key.startsWith(`${index}-`)) {
        const vendorDetailIndex = key.split("-")[1];
        const vendorDetailOriginal = vendorDetailsCopy[vendorDetailIndex] || {};
        const revisionAmount = value?.revisionAmount ? parseFloat(value?.revisionAmount) : undefined;
        const vendorDetailWithFormInputs = { ...vendorDetailOriginal, ...value, revisionAmount };
        if (payeeOrganizationId && payeeOrganizationDisplay && detailsIndex === index && parseInt(vendorDetailIndex) === vendorDetailsCopy.length - 1) {
          const updatedVendorDetail = { ...vendorDetailWithFormInputs, payeeOrganizationDisplay, payeeOrganizationId };
          if (!vendorDetailOriginal.payeeOrganizationId || editedVendorId) {
            updatedVendorDetails.push(updatedVendorDetail);
            if (setInputs) {
              setInputs({
                ...inputs,
                [key]: updatedVendorDetail,
              });
            }
          }
          else {
            updatedVendorDetails.push({ ...vendorDetailWithFormInputs, isSelected: false });
            updatedVendorDetails.push({ payeeOrganizationDisplay, payeeOrganizationId, isSelected: true });
          }
        }
        else {
          updatedVendorDetails.push(vendorDetailWithFormInputs);
        }

        remainingVendorDetails.splice(vendorDetailIndex, 1);
      }
    });

    if (!payeeOrganizationId && remainingVendorDetails.length > 0) {
      updatedVendorDetails.push(...remainingVendorDetails);
    }

    return updatedVendorDetails;
  };

  const getUpdatedTempData = (loanDrawRevisedDetails = drawDetails, formOptions, addedVendor = {} as IAddedVendorData, editedVendorId = "") => {
    const { inputs, setInputs, metaData } = formOptions;
    const reviseData = metaData?.reviseData || loanDrawRevisedDetails;
    const loanDrawRevisedBudgetDetails = reviseData?.loanDrawTempBudgetDetails || [];
    return {
      ...reviseData,
      loanDrawTempBudgetDetails: loanDrawRevisedBudgetDetails.map((budgetDetail, index) => {
        const vendorDetailsCopy = budgetDetail.loanDrawTempBudgetDetailVendors;
        const detailsInputs = inputs ? inputs[index] || {} : {};
        return {
          ...budgetDetail,
          ...detailsInputs,
          loanDrawTempBudgetDetailVendors: getUpdatedVendorRowDetails(inputs, index, vendorDetailsCopy, setInputs, addedVendor, editedVendorId)
        };
      })
    };
  };

  const getBudgetDetailsIdsWithApprovedAmount = (inputs) => {
    if (loanDrawBudgetDetails) {
      return loanDrawBudgetDetails.map((rowData, i) => {
        const approvedAmount = inputs[i].drawApprovedAmount !== undefined ? parseFloat(inputs[i].drawApprovedAmount) : 0;
        return {
          loanBudgetDetailId: rowData.id,
          approvedAmount
        };
      });
    }

    return [];
  };

  const handleEditVendor = (options, index, vendorIndex) => {
    if (FeatureFlags.IS_DEBUG) {
      console.log(options, index, vendorIndex); // TODO: Add logic to edit vendor
    }
  };

  const handleRemoveVendor = (options, index, vendorIndex) => {
    if (FeatureFlags.IS_DEBUG) {
       console.log(options, index, vendorIndex); // TODO: Add logic to remove vendor
    }
  };

  const loanInfoHeaderForm = new BCFormController({ ...(drawData || {}), drawComment: loanDrawComments?.[loanDrawComments.length -1]?.description }, { metaData: { bankInformationId } });
  loanInfoHeaderForm.addGroupFields([
    loanInfoHeaderForm.getDropdownDisplayField("Borrower", { name: "borrowerOrganizationName", required: true }),
    loanInfoHeaderForm.getDropdownDisplayField("Loan", { name: "loanDisplay", required: true }),
    loanInfoHeaderForm.getDropdownDisplayField("Loan Property", { name: "loanPropertyDisplay", required: true }),
    loanInfoHeaderForm.getCheckboxField("Direct Pay", { disabled: true })
  ]);
  loanInfoHeaderForm.addGroupFields([
    loanInfoHeaderForm.getTextareaField("Description", { width: 8 }),
    loanInfoHeaderForm.getDisplayField("Draw Comment", { width: 4 }),
    loanInfoHeaderForm.getButtonField("Add Comment", undefined, {
      isEnabled: true,
      modalContent: (setIsAddFormModalOpen) => (
        <AddLoanDetailsCommentModal
          loanId={drawData?.loanId}
          loanDrawId={drawData?.id}
          setIsModalOpen={setIsAddFormModalOpen}
          setNewCommentId={setNewCommentId}
        />
      ),
    }),
  ], { widths: 8 });
  loanInfoHeaderForm.addDividerField({ dividerIsHidden: true });
  const sendToBorrowerComment = isRequiresBorrowerRevision
    ? [loanInfoHeaderForm.getTextareaField("Send To Borrower Comment", { name: "sendToBorrowerComment", width: 8 })]
    : [];

  loanInfoHeaderForm.addGroupFields([
    loanInfoHeaderForm.getTextareaField("Draw Without Inspection Comment", { name: "drawInspectionComment", width: 8 }),
    ...sendToBorrowerComment
  ], { widths: 8 });
  loanInfoHeaderForm.addGroupFields([
    loanInfoHeaderForm.getDropdownDisplayField("Bank", { name: "bankInformationDisplay", width: 4 }),
    loanInfoHeaderForm.getComponent(({ metaData }) => {
      return <LmsTable
        formatDataFunc={formatBankDataFunc}
        apiSuffix={metaData?.bankInformationId ? `draws/bank-info?loanDrawId=${id}&bankInformationId=${metaData?.bankInformationId}` : ""}
        key="bank-info-table"
        isDisabled
      />;
    }, { width: 12 }),
  ], { widths: 1 });

  if (drawData?.workInspectionPending) {
    loanInfoHeaderForm.addCenteredGroup([loanInfoHeaderForm?.getWarningTextField("Work Inspection Pending", { position: "center" })]);
  }

  const myForm = new BCFormController(drawDetails, { isCompact: true, styles: { minWidth: "2500px" } });
  const submitButtons = [
    myForm.getDisplayField("Draw Name", {
      width: 2,
      name: "loanDrawName",
      inline: true
    }),
  ];

  const buttonsOptions = { alignLeft: true, fullWidth: true, isCompact: true, buttonIcon: GlobalConstants.Icons.CARET_UP };
  if (!isRequiresBorrowerRevision) {
    submitButtons.push(myForm.getButtonField("Approve", handleApprove, {
      width: 1,
      ...buttonsOptions,
      buttonIcon: GlobalConstants.Icons.SAVE,
    }));
    submitButtons.push(myForm.getButtonField("Send Back To Borrower", handleSendBackToBorrower, { width: 2, ...buttonsOptions }));
  }

  if (isRequiresBorrowerRevision) {
    submitButtons.push(myForm.getButtonField("Send To Borrower", (options) => handleSendBackToBorrower(options, "draws/submit-to-borrower"), {
      width: 2,
      ...buttonsOptions
    }));
  }

  myForm.addGroupFields([...submitButtons], { widths: 2 });
  myForm.addDividerField({ dividerIsHidden: true });

  const labelFields = [] as any[];
  labelFields.push(myForm.getLabelField("Budget Category", { width: 3, position: "center" }));
  labelFields.push(myForm.getLabelField("Sub-Category", { width: 2, position: "center" }));
  labelFields.push(myForm.getLabelField("Direct Pay", { width: 1, position: "center" }));
  labelFields.push(myForm.getLabelField("Vendor", { width: 2, position: "center" }));
  labelFields.push(myForm.getLabelField("Is Paid", { width: 1, position: "center" }));
  labelFields.push(myForm.getLabelField("Paid By", { width: 1, position: "center" }));
  labelFields.push(myForm.getLabelField("Requested Amount", { width: 2, position: "center" }));
  labelFields.push(myForm.getLabelField("Approved Amount", { width: 2, position: "center" }));
  labelFields.push(myForm.getLabelField("Available (Previously Inspected)", { width: 2, wrapLabel: true, position: "center" }));
  labelFields.push(myForm.getLabelField("% Work Complete", { width: 2, position: "center" }));
  labelFields.push(myForm.getLabelField("Invoice #/ Reference", { width: 2, position: "center" }));
  labelFields.push(myForm.getLabelField("Comments", { width: 2, position: "center" }));
  myForm.addGroupFields(labelFields, { widths: 1 });

  if (loanDrawBudgetDetails) {
    loanDrawBudgetDetails.map((rowData, i) => {
      const {
        loanBudgetCategoryDisplay,
        loanBudgetSubCategoryDisplay,
        detailDirectPay,
        drawRequestedAmount,
        description,
        drawRevisionAmount,
        drawApprovedAmount,
        loanDrawTempBudgetDetailVendors,
        budgetDetailPercentWorkComplete,
        budgetAmount
      } = rowData;

      const formRowFields = [] as any[];
      formRowFields.push(myForm.getLabelField(loanBudgetCategoryDisplay, { width: 3, position: "top-left", wrapLabel: true, fontWeightLight: true }));
      formRowFields.push(myForm.getLabelField(loanBudgetSubCategoryDisplay, { width: 2, position: "top-left", wrapLabel: true, fontWeightLight: true }));
      formRowFields.push(myForm.getCheckboxField("Direct Pay", { label: "", value: detailDirectPay, width: 1, position: "center", nestedInputsProp: i }));
      formRowFields.push(myForm.getButtonField("", undefined, {
        modalContent: (setIsAddFormModalOpen, options) => (
          <AddVendorToReviseDrawForm
            reviseData={drawDetails}
            setIsAddVendorOpen={setIsAddFormModalOpen}
            reviseFormOptions={options}
            getUpdatedTempData={getUpdatedTempData}
            detailNestedInputsProp={i}
            setReviseData={setData}
          />
        ),
        buttonIcon: GlobalConstants.Icons.ADD,
        width: 2,
        nestedInputsProp: i
      }));
      formRowFields.push(myForm.getBlankField({ width: 1 }));
      formRowFields.push(myForm.getBlankField({ width: 1 }));
      formRowFields.push(myForm.getCurrencyField("Requested Amount", {
        label: "",
        name: "drawRequestedAmount",
        value: drawRequestedAmount,
        calculatedTargetField: "requestedAmountTotal",
        width: 2,
        disabled: true,
        nestedInputsProp: i
      }));
      formRowFields.push(myForm.getCurrencyField("Amount Approved", {
        label: "",
        name: "drawApprovedAmount",
        calculatedTargetField: "approvedAmountTotal",
        value: drawApprovedAmount || 0,
        width: 2,
        disabled: true,
        nestedInputsProp: i
      }));
      formRowFields.push(myForm.getCurrencyField("Available (Previously Inspected)", {
        label: "",
        name: "availablePreviouslyInspected",
        value: budgetAmount,
        width: 2,
        disabled: true,
        nestedInputsProp: i
      }));
      formRowFields.push(myForm.getPercentField("% Work Complete", 100, {
        label: "",
        name: "budgetDetailPercentWorkComplete",
        value: budgetDetailPercentWorkComplete || 0,
        width: 2,
        decimalsLimit: 2,
        disabled: true,
        nestedInputsProp: i
      }));
      formRowFields.push(myForm.getBlankField({ width: 2 }));
      formRowFields.push(myForm.getInputField("Comments", { label: "", name: "description", value: description, width: 2, nestedInputsProp: i }));
      myForm.addGroupFields(formRowFields, { widths: 1 });

      const vendors = loanDrawTempBudgetDetailVendors || [];
      if (vendors.length === 0) {
        vendors.push({
          approvedAmount: drawApprovedAmount,
          description: "",
          drawReference: null,
          isPaid: false,
          isSelected: false,
          paymentMethodOption: "",
          revisionAmount: drawRevisionAmount,
          requestedAmount: drawRequestedAmount,
        });
      }

      if (vendors?.length > 0) {
        vendors.map((vendorDetail, vendorIndex) => {
          const {
            approvedAmount: approvedVendorAmount,
            description: descriptionVendor,
            drawReference: drawReference,
            isPaid,
            paymentMethodOption: paymentMethodOptionVendor,
            requestedAmount: requestedAmountVendor,
            payeeOrganizationDisplay,
            payeeOrganizationId,
          } = vendorDetail;
          const nestedInputsProp = `${i}-${vendorIndex}`;
          const vendorDetailsRowFields = [] as any[];
          vendorDetailsRowFields.push(myForm.getBlankField({ width: 3 }));
          vendorDetailsRowFields.push(myForm.getBlankField({ width: 2 }));
          vendorDetailsRowFields.push(myForm.getBlankField({ width: 1 }));
          vendorDetailsRowFields.push(myForm.getButtonsGroupField([
            myForm.getButtonField("", (options) => handleEditVendor(options, i, vendorIndex), {
              name: "editVendor",
              buttonIcon: GlobalConstants.Icons.EDIT,
              modalContent: (setIsAddFormModalOpen, options) => (
                <AddVendorToReviseDrawForm
                  reviseData={drawDetails}
                  setIsAddVendorOpen={setIsAddFormModalOpen}
                  reviseFormOptions={options}
                  getUpdatedTempData={getUpdatedTempData}
                  detailNestedInputsProp={i}
                  setReviseData={setData}
                  value={payeeOrganizationId}
                />
              ),
            }) as any,
            myForm.getButtonField("", (options) => handleRemoveVendor(options, i, vendorIndex), {
              name: "removeVendor",
              buttonIcon: GlobalConstants.Icons.DELETE,
              fullWidth: true,
            }) as any,
            myForm.getButtonField("", undefined, {
              name: "payeeOrganizationDisplay",
              value: payeeOrganizationDisplay,
              calculatedTargetField: "calculatedVendorsTotal",
              modalContent: () => <LmsTable actionButtons={["export"]} apiSuffix={`orgs/bank-infolist/${payeeOrganizationId}`} />,
              modalSize: "fullscreen",
              buttonRenderType: "link"
            }) as any
          ], { width: 2 }));
          vendorDetailsRowFields.push(myForm.getCheckboxField("", { name: "isPaid", value: isPaid, width: 1, nestedInputsProp, position: "center" }));
          vendorDetailsRowFields.push(myForm.getDropdownField("", DrawsPaymentMethodOptions, {
            width: 1,
            placeHolder: "Select",
            value: paymentMethodOptionVendor || DrawsPaymentMethodOptions[0].value,
            label: "",
            name: "paymentMethodOption",
            skipClearable: true,
            nestedInputsProp
          }));
          vendorDetailsRowFields.push(myForm.getCurrencyField("Requested Amount", {
            label: "",
            name: "requestedAmount",
            value: requestedAmountVendor,
            calculatedTargetField: "requestedAmountTotal",
            width: 2,
            nestedInputsProp
          }));
          vendorDetailsRowFields.push(myForm.getCurrencyField("Amount Approved", {
            label: "",
            name: "drawApprovedAmount",
            calculatedTargetField: "approvedAmountTotal",
            value: approvedVendorAmount,
            width: 2,
            nestedInputsProp
          }));
          vendorDetailsRowFields.push(myForm.getBlankField({ width: 2 }));
          vendorDetailsRowFields.push(myForm.getBlankField({ width: 2 }));
          vendorDetailsRowFields.push(myForm.getInputField("Invoice #/ Reference", {
            label: "",
            name: "drawReference",
            value: drawReference,
            width: 2,
            disabled: true,
            nestedInputsProp
          }));
          vendorDetailsRowFields.push(myForm.getInputField("Comments", { label: "", name: description, value: descriptionVendor, width: 2, nestedInputsProp }));
          myForm.addGroupFields(vendorDetailsRowFields, { widths: 1, nestedInputsProp });
        });
      }
    });
  }

  const calculateVendorsTotal = (_, options) => {
    let total = 0;
    options.formFields?.map(({ groupFields }) => {
      if (groupFields && groupFields[3] && groupFields[3].name === "payeeOrganizationDisplay" && groupFields[3].value) {
        total++;
      }
    });

    return total;
  };

  const handleWaiveOrderClick = ({ updateInput }, updateName) => {
    updateInput(updateName, new Date().toISOString());
  };

  const totalsFormRowFields = [] as any[];
  totalsFormRowFields.push(myForm.getBlankField({ width: 2 }));
  totalsFormRowFields.push(myForm.getBlankField({ width: 1 }));
  totalsFormRowFields.push(myForm.getBlankField({ width: 1 }));
  totalsFormRowFields.push(myForm.getLabelField("Vendor Total", { position: "center", width: 2 }));
  totalsFormRowFields.push(myForm.getNumberField("", {
    label: "",
    name: "calculatedVendorsTotal",
    calculatedFunc: calculateVendorsTotal,
    width: 2,
    disabled: true,
  }));
  totalsFormRowFields.push(myForm.getBlankField({ width: 1 }));
  totalsFormRowFields.push(myForm.getLabelField("Totals", { position: "center", width: 1 }));
  totalsFormRowFields.push(myForm.getCurrencyField("Requested Amount Total", { label: "", name: "requestedAmountTotal", width: 2, disabled: true }));
  totalsFormRowFields.push(myForm.getCurrencyField("Approved Amount Total", { label: "", width: 2, disabled: true }));
  totalsFormRowFields.push(myForm.getBlankField({ width: 2 }));
  totalsFormRowFields.push(myForm.getBlankField({ width: 2 }));
  totalsFormRowFields.push(myForm.getBlankField({ width: 2 }));
  totalsFormRowFields.push(myForm.getBlankField({ width: 2 }));
  myForm.addGroupFields(totalsFormRowFields, { widths: 1 });

  myForm.addDividerField({ dividerIsHidden: true });
  myForm.addGroupFields([
    myForm.getButtonField("Modify Budget", undefined, {
      modalContent: (setIsAddFormModalOpen) => (
        <LmsModifyBudget id={loanPropertyId} handleClose={() => setIsAddFormModalOpen(false)}/>
      ),
      modalSize: "fullscreen",
      fullWidth: true,
      width: 2
    }),
    myForm.getButtonField("Referesh", handleRefresh, { buttonIcon: GlobalConstants.Icons.CARET_UP, fullWidth: true, width: 2   }),
    myForm.getBlankField({ width: 2 }),
    myForm.getButtonField("Add Exisiting Line Item", undefined, {
      modalContent: (setIsModalOpen, formOptions) => {
        return (
          <AddExistingLineTable
            loanDrawTempId={loanDrawTempId}
            setAddExistingIsVisible={setIsModalOpen}
            useFormInstance={formOptions}
            getUpdatedTempData={getUpdatedTempData}
            revisedDrawDetails={drawDetails}
            setData={setData}
          />
        );
      },
      buttonIcon: GlobalConstants.Icons.CARET_UP,
      fullWidth: true,
      width: 2
    }),
    myForm.getFileField("Upload", { width: 6, inline: true }),
    myForm.getBlankField({ width: 4 }),
  ]);

  myForm.addDividerField({ dividerIsHidden: true });
  myForm.addDividerField();

  myForm.addGroupFields([
    myForm.getButtonField("Order Date Down", (options) => handleWaiveOrderClick(options, "orderDateDownDate"), {
      alignRight: true,
      buttonIcon: GlobalConstants.Icons.CARET_UP,
      width: 2
    }),
    myForm.getCalendarField("Order Date Down", { label: "", name: "orderDateDownDate", width: 2 }),
    myForm.getButtonField("Order Draw Inspection", undefined, {
      modalContent: () => (
        <LmsCompanyInspectionsTable suffix={`draws/${id}/inspections`} />
      ),
      alignRight: true,
      buttonIcon: GlobalConstants.Icons.CARET_UP,
      width: 2
    }),
    myForm.getBlankField({ width: 2 }),
    myForm.getBlankField({ width: 1 }),
    myForm.getLabelField("Date Down Fee", { width: 1, position: "right-center" }),
    myForm.getCurrencyField("Date Down Fee", { label: "", width: 1 }),
    myForm.getLabelField("Title #", { position: "right-center", width: 1 }),
    myForm.getBlankField({ width: 1 }),
  ], { widths: 1 });


  myForm.addGroupFields([
    myForm.getButtonField("Wave Date Down", ({ setInputs, inputs }) => {
      setInputs({ ...inputs, orderDateDownDate: undefined, waveDateDown: new Date().toISOString() });
    }, {
      alignRight: true,
      buttonIcon: GlobalConstants.Icons.CARET_UP,
      width: 2
    }),
    myForm.getCalendarField("Wave Date Down", { label: "", width: 2 }),
    myForm.getButtonField("Wave Draw Inspection", (options) => handleWaiveOrderClick(options, "waveDateInspection"), {
      alignRight: true,
      buttonIcon: GlobalConstants.Icons.CARET_UP,
      width: 2
    }),
    myForm.getCalendarField("Wave Date Inspection", { label: "", width: 2 }),
    myForm.getBlankField({ width: 1 }),
    myForm.getLabelField("Inspection Fee", { width: 1, position: "right-center" }),
    myForm.getCurrencyField("Inspection Fee", { label: "", width: 1 }),
    myForm.getLabelField("Original Loan Amount", { width: 1, position: "right-center" }),
    myForm.getCurrencyField("Original Loan Amount", { label: "", width: 1 }),
  ], { widths: 1 });

  myForm.addGroupFields([
    myForm.getLabelField("Date Down Received", { position: "right-center", width: 2 }),
    myForm.getCalendarField("Date Down Received Date", { label: "", width: 2  }),
    myForm.getLabelField("Inspection Received", { position: "right-center", width: 2 }),
    myForm.getCalendarField("Inspection Received Date", { label: "", width: 2 }),
    myForm.getBlankField({ width: 1 }),
    myForm.getLabelField("Draw Processing Fee", { width: 1, position: "right-center" }),
    myForm.getCurrencyField("Draw Processing Fee", { label: "", width: 1 }),
    myForm.getLabelField("Current Loan Balance", { width: 1, position: "right-center" }),
    myForm.getCurrencyField("Current Loan Balance", { label: "", name: "currentAmountPending", width: 1, disabled: true }),
  ]);

  myForm.addDividerField({ dividerIsHidden: true });
  myForm.addGroupFields([
    myForm.getComponent(<LmsTable apiSuffix={`draws/${id}/work-inspections`} isDisabled key={`draws/${id}/work-inspections`} />, { width: 6 }),
    myForm.getBlankField({ width: 2 }),
    myForm.getBlankField({ width: 1 }),
    myForm.getCurrencyField("Inspection Site Visit Fee", { width: 2, inline: true }),
    myForm.getCalendarField("Current Maturity Date", { width: 2, name: "maturityDateCurrent", disabled: true, inline: true }),
  ]);

  myForm.addGroupFields([
    myForm.getBlankField({ width: 2 }),
    myForm.getTextareaField("Date Down Received Comment", { label: "", width: 3 }),
    myForm.getBlankField({ width: 2 }),
    myForm.getTextareaField("Inspection Received Comment", { label: "", width: 3 }),
    myForm.getBlankField(),
    myForm.getBlankField(),
  ]);

  myForm.addDividerField();
  setInspectionSectionFields(myForm);
  myForm.addDividerField();

  if (loanDrawComments?.length > 0) {
    myForm.addGroupFields([
      myForm.getLabelField("Subject", { width: 2, position: "center" }),
      myForm.getLabelField("Date", { width: 2, position: "center" }),
      myForm.getLabelField("Comments", { width: 2, position: "center" }),
    ], { widths: 3 });
    loanDrawComments.forEach((comment, index) => {
      myForm.addGroupFields([
        myForm.getInputField("Subject", { label: "", name: "commentSubject", width: 2, value: comment.commentSubject, nestedInputsProp: `comment-${index}`, disabled: true }),
        myForm.getCalendarField("Date", { label: "", name: "commentDate", width: 1, value: comment.commentedOn, nestedInputsProp: `comment-${index}`, disabled: true }),
        myForm.getTextareaField("Comments", { label: "", width: 3, value: comment.description, nestedInputsProp: `comment-${index}`, disabled: true }),
      ], { widths: 3 });
    });
  }

  const handleSave = async (inputs, options) => {
    const updatedTempData = getUpdatedTempData(inputs, options);
    const useFormInstance = options.useFormInstance;
    useFormInstance.initSubmittingStates(options.buttonText);

    const validateDrawPayload = {
      loanDrawTempId: drawDetails?.id,
      orderDateDownDate: new Date().toLocaleDateString(),
      totalDrawAmount: inputs?.requestedAmountTotal,
      validateInspection: false
    };

    const { data, error } = await fetchData("draws/validate-save", { fetchMethod: "POST", data: validateDrawPayload });
    if (error) {
      setError(error);
      useFormInstance.resetSubmittingStates();
    }

    if (!error && data) {
      if (!data.existsCommitmentCap) {
        options.handleSubmit("draws/save", { method: "PUT", data: updatedTempData, buttonText: options.buttonText });
      }
      else {
        useFormInstance.resetSubmittingStates();
      }
    }
  };

  const handleVoidDrawClick = (inputs, _, options) => {
    const confirmVoidPayload = {
      loanDrawId: id,
      inspectionFee: inputs?.inspectionFee,
      dateDownFee: inputs?.dateDownFee
    };

    options.handleConfirmFunc(async () => {
      options.initSubmittingStates(options.buttonText);
      const { data: resData, error: resError } = await fetchData("draws/confirm-void-draw", {
        fetchMethod: "POST",
        data: confirmVoidPayload
      });

      if (resError) {
        setError(resError);
      }
      else {
        if (resData.confirmMessage) {
          if (confirm(resData.confirmMessage)) {
            setVoidValues({ ...confirmVoidPayload, loanDrawTempId });
            setIsVoidFormModalOpen(true);
          }
        }
        else {
          setVoidValues({ ...confirmVoidPayload, loanDrawTempId });
          setIsVoidFormModalOpen(true);
        }
      }

      options.setConfirmOptions({});
    }, "Are you sure you want to Void this Draw?");
  };

  const formActions: FormActionsType = [
    {
      text: "Void Draw",
      handleClick: handleVoidDrawClick,
      icon: GlobalConstants.Icons.CARET_UP,
    },
    {
      text: "Download Management",
      action: GlobalConstants.FormDefaultAction.DOWNLOAD,
      path: "draws/export-histories",
      icon: GlobalConstants.Icons.SAVE,
    },
    "save",
    "save-close",
    "close"
  ];

  return <>
    <LmsFormGenerator myForm={loanInfoHeaderForm} skipActions isLoading={isLoading} />
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      error={error}
      actions={formActions}
      handleSubmit={handleSave}
    />
    <BCModal
      isModalOpen={IsDrawExceedsPromptOpen}
      onClose={() => setIsDrawExceedsPromptOpen(false)}
      closeIcon
      content="Draw Request exceeds the commitment cap for this loan. Please review commitment cap and see servicing team."
      buttonText="Ok"
    />
    <BCModal
      isModalOpen={isVoidFormModalOpen}
      onClose={() => setIsVoidFormModalOpen(false)}
      closeIcon
      content={<VoidReasonForm values={voidValues} setIsVoidFormModalOpen={setIsVoidFormModalOpen} />}
    />
  </>;
};

const DrawEditFeeForm = ({ id, handleClose }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`draws/${id}/summary`);
  const myForm = new BCFormController(data, {
    values: data,
    payloadDataType: "FormData",
    submitSuffix: "draws/void-draw",
    dataFormatterFunc: ({
      dateDownFee,
      effectiveDate,
      inspectionFee,
      voidDocument,
      voidReason,
    }) => ({ dateDownFee, effectiveDate, inspectionFee, loanDrawId: id, voidDocument, voidReason }),
  });

  myForm.addGroupFields([
    myForm.getDisplayField("Loan #", { name: "loanNumber", noBackground: true }),
    myForm.getCalendarField("Date Down Order", { name: "orderDateDownDate", noBackground: true }),
    myForm.getCalendarField("Date Down Received", {
      name: "dateDownReceivedDate",
      noBackground: true,
    }),
    myForm.getCalendarField("Date Down Waived", { name: "waiveDateDownDate", noBackground: true }),
  ]);

  myForm.addGroupFields([
    myForm.getDisplayField("Draw Name", { name: "name", noBackground: true }),
    myForm.getCalendarField("Inspection Order Date", {
      name: "orderDrawInspectionDate",
      noBackground: true,
    }),
    myForm.getCalendarField("Inspection Received", {
      name: "inspectionReceivedDate",
      noBackground: true,
    }),
    myForm.getCalendarField("Inspection Waived Date", {
      name: "waiveDrawInspectionDate",
      noBackground: true,
    }),
  ]);

  myForm.addGroupFields([
    myForm.getCurrencyField("Date Down Fee", { value: 0 }),
    myForm.getCurrencyField("Inspection Fee", { value: 0 }),
    myForm.getCalendarField("Effective Date", { required: true, isDefaultDateNow: true }),
    myForm.getFileField("Void Document"),
  ]);

  myForm.addTextareaField("Void Reason", { required: true });

  return (
    <LmsFormGenerator
      error={error}
      myForm={myForm}
      isLoading={isLoading}
      handleClose={handleClose}
      onSubmitSuccessMessage="Loan Draw Voided."
      actions={[{ text: "Void Draw", action: "save-close" }, "close"]}
    />
  );
};

export const ViewDrawForm = (
  {
    id,
    drawData = {} as any,
    drawInfo = {} as any,
    handleClose = undefined as any,
  }
) => {
  const [voidMessage, setVoidMessage] = useState<string | null>(null);
  const [isVoidDrawOpen, setIsVoidDrawOpen] = useState(false);
  const { errorTitle, isLoanPhaseWaitingForBorrowerApproval } = drawInfo;
  const myForm = addViewDrawFormFields(
    new BCFormController(drawData, {
      values: drawData,
      initIsDisabled: true,
      errorTitle: drawInfo.errorTitle,
    }),
  );

  const handleCancelConfirm = () => {
    setVoidMessage(null);
  };

  const handleCloseVoidDraw = () => {
    setIsVoidDrawOpen(false);
  };

  const handleConfirmVoidDraw = (
    { id: loanDrawId, inspectionFee, dateDownFee },
    handleSubmit,
    options
  ) => {
    handleSubmit("draws/confirm-void-draw", {
      method: "POST",
      data: {
        loanDrawId,
        dateDownFee: dateDownFee || 0,
        inspectionFee: inspectionFee || 0,
      },
      buttonText: options.buttonText,
      onSuccessFunc: (data) => setVoidMessage(data.confirmMessage),
    });
  };

  return (
    <React.Fragment>
      <Confirm
        size="tiny"
        content={voidMessage}
        open={voidMessage !== null}
        onCancel={handleCancelConfirm}
        onConfirm={() => {
          handleCancelConfirm();
          setIsVoidDrawOpen(true);
        }}
      />
      <LmsFormGenerator
        myForm={myForm}
        actions={
          errorTitle || isLoanPhaseWaitingForBorrowerApproval
            ? [{ text: "Void Draw", handleClick: handleConfirmVoidDraw }, "close"]
            : ["close"]
        }
        handleClose={handleClose}
      />
      <BCModal
        closeIcon
        size="large"
        isModalOpen={isVoidDrawOpen}
        onClose={handleCloseVoidDraw}
        content={<DrawEditFeeForm id={id} handleClose={handleCloseVoidDraw}/>}
      />
      {drawData.loanId && <EditFormTabTables id={id} loanId={drawData.loanId} />}
    </React.Fragment>
  );
};

const EditDrawDetailsForm = ({ id, drawData, drawInfo }) => {
    const [newCommentId, setNewCommentId] = useState("");
  return <>
    {drawInfo?.drawStatus === "New" && <ViewOriginalDraw id={id} isNewDraw />}
    {drawInfo?.drawStatus !== "New" && <>
      <AddDrawRequestedDetailsForm
        id={id}
        drawData={drawData}
        setNewCommentId={setNewCommentId}
      />
      {drawData?.loanId && <RequestedFormTabTables 
        id={id} 
        loanId={drawData?.loanId} 
        newCommentId={newCommentId}
        setNewCommentId={setNewCommentId}
      />}
    </>}
  </>;
};

const EditDrawForm = ({ id }) => {
  const [{ data, isLoading }] = useFetchData(`draws/${id}`);
  const drawStatus = data?.loanDrawStatusDisplay;
  const hasDetails = drawStatus === "New" || drawStatus === "Requested" || drawStatus === "Requires Borrower Revision" || drawStatus === "Revised Request";
  const isLoanPhasePaidOff = data?.loanPhaseDisplay == "Paid Off";
  const isLoanPhaseWaitingForBorrowerApproval = (data?.loanPhaseDisplay || "").toLowerCase() === "waiting for borrower approval";
  const errorTitle = isLoanPhasePaidOff
    ? "Loan is Paid Off"
    : data?.isFrozen
      ? "Loan is frozen"
      : data?.isLoanPropertyFrozen
        ? "Property is frozen"
        : data?.allUnitsPaid
          ? "All units paid off for the property"
          : undefined;

  const drawInfo = {
    drawStatus,
    errorTitle,
    isLoanPhaseWaitingForBorrowerApproval
  };

  return <>
    {isLoading && <BCLoader />}
    {data && !hasDetails && <ViewDrawForm id={id} drawData={data} drawInfo={drawInfo} />}
    {data && hasDetails && <EditDrawDetailsForm id={id} drawData={data} drawInfo={drawInfo} />}
  </>;
};

const LmsServicingDraws = ({ params = {}, history }) => {
  const allowedActions = [];
  const allowedSubpages = ["revise", "summary", "modify-budget"];
  const { isMainPage, isAdd, isEdit, isActionAllowed, isSubpageAllowed, actionLower, id, query } = useParseRouteActions(params, allowedActions, allowedSubpages);
  const loanId = query.get("loanId") || "";

  if (!isActionAllowed) {
    history.goBack();
  }

  return (<React.Fragment>
    {isMainPage && <MainServicingDrawTable />}
    {isAdd && <AddDrawForm loanId={loanId} />}
    {isEdit && <EditDrawForm id={id} />}
    {isSubpageAllowed && <React.Fragment>
      {actionLower === "revise" && <ReviseLoanDraw id={id} />}
      {actionLower === "summary" && <SummarySubpage id={id} />}
    </React.Fragment>}
  </React.Fragment>);
};

export default LmsServicingDraws;